import React, { Suspense, lazy, useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { format, addDays, parseISO, set, add, startOfDay, isWithinInterval, isSameDay, parse } from 'date-fns';
import { toZonedTime, format as formatTz } from 'date-fns-tz';
import { createCampaign, createRCFCampaign, updateRCFCampaign, getRCFCampaignById, getCampaigns, checkForOverlap } from '../../services/campaignService';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { getCustomIngredients, saveCustomIngredient, deleteCustomIngredient } from '../../services/customIngredientsService';
import { getCustomAngles, saveCustomAngle, deleteCustomAngle } from '../../services/customAnglesService';
import type { Campaign as InternalCampaignType, Campaign } from '../../types/Campaign';
import useDocumentTitle from '../../utils/useDocumentTitle';
// Add import for react-datepicker at the top of the file
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Define types for items and groups
type ItemType = 'objection' | 'benefit';

type Item = {
  id: string;
  type: ItemType;
  content: string;
};

type Group = {
  id: string;
  itemIds: string[];
};

// Section state interfaces
interface OfferCreationState {
  campaignName: string;
  objective: string;
  loeChoice: 'Low' | 'Medium' | 'High';
  offerIngredients: string[];
  startDate?: Date;
}

interface LOECalculatorState {
  emailClicks: string;
  conversionRate: string;
  averageOrderValue: string;
  unitCost: string;
  hardCosts: string;
  personnelCosts: string;
  daysToImplement: string;
  relationshipScore: number;
  opportunityCostPreset: 'Low' | 'Medium' | 'High';
}

interface SequenceMappingState {
  productName: string;
  productPrice: string;
  uniqueMechanism: string;
  resultTheyWant: string;
  mainBelief: string;
  reasonForPromo: string;
  subObjective: 'Revenue' | 'Volume';
}

interface ContextualValueAnglesState {
  contentEmailCount: number;
  emailAssignments: Record<number, string[]>;
}

interface CalculatedResultsState {
  revenueTotal: number;
  loeValue: number;
  marketingProfit: number;
  hasValidInputs: boolean;
  loeCategory: string;
}

interface ValidationState {
  offerCreation: {
    campaignName: boolean;
    clearObjective: boolean;
    selectedLOE: boolean;
    atLeastOneIngredient: boolean;
    hasStartDate: boolean;
    isValid: boolean;
  };
  loeCalculator: {
    emailClicks: boolean;
    conversionRate: boolean;
    averageOrderValue: boolean;
    hardCosts: boolean;
    personnelCosts: boolean;
    daysToImplement: boolean;
    relationshipScore: boolean;
    opportunityCostPreset: boolean;
    isValid: boolean;
  };
  sequenceMapping: {
    productName: boolean;
    productPrice: boolean;
    uniqueMechanism: boolean;
    resultTheyWant: boolean;
    mainBelief: boolean;
    reasonForPromo: boolean;
    subObjective: boolean;
    hasAtLeastOneGroup: boolean;
    isValid: boolean;
  };
  contextualValueAngles: {
    contentEmailCount: boolean;
    hasAtLeastOneAngleAssigned: boolean;
    isValid: boolean;
  };
  allValid: boolean;
}

interface FormState {
  offerCreation: OfferCreationState;
  loeCalculator: LOECalculatorState;
  sequenceMapping: SequenceMappingState;
  contextualValueAngles: ContextualValueAnglesState;
  groups: Record<string, Group>;
  items: Record<string, Item>;
}

// Create a simple toast function if the UI library isn't available
const useCustomToast = () => {
  const toastMessages = (options: { 
    title: string; 
    description: string; 
    status: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
    isClosable?: boolean;
  }) => {
    // Create a simple alert for now as a fallback
    const { title, description, status } = options;
    const message = `${title}: ${description}`;
    
    if (status === 'error') {
      console.error(message);
    } else {
      console.log(message);
    }
    
    // Simple browser alert with the message
    // We can enhance this with a custom toast UI component later
    alert(message);
  };
  
  return toastMessages;
};

interface RCFCampaignPlannerPageProps {
  dashboardModalActive?: boolean;
}

/**
 * Calls the server API to calculate LOE and revenue metrics
 * This protects our proprietary calculation logic
 * 
 * @param inputs LOE calculator inputs
 * @returns Calculation results from the server
 */
async function calculateLOERemote(inputs: {
  emailClicks: string;
  conversionRate: string;
  averageOrderValue: string;
  hardCosts: string;
  personnelCosts: string;
  daysToImplement: string;
  relationshipScore: number;
  opportunityCostPreset: 'Low' | 'Medium' | 'High';
}): Promise<{
  revenueTotal: number;
  loeValue: number;
  marketingProfit: number;
  hasValidInputs: boolean;
  loeCategory: string;
}> {
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/calculate-loe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send cookies for authentication
      body: JSON.stringify(inputs),
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('LOE calculation error:', error);
    // Return fallback values to avoid breaking the UI
    return {
      revenueTotal: 0,
      loeValue: 0,
      marketingProfit: 0,
      hasValidInputs: false,
      loeCategory: 'Unknown'
    };
  }
}

/**
 * RCFCampaignPlannerPage - Main component for the Repeat Customer Formula Campaign Planner
 * 
 * This page implements the 4-step workflow for creating email campaigns using the
 * Repeat Customer Formula methodology:
 * 1. Offer Creation
 * 2. LOE Calculator 
 * 3. Sequence Mapping
 * 4. Contextual Value Angles
 */
const RCFCampaignPlannerPage: React.FC<RCFCampaignPlannerPageProps> = ({ dashboardModalActive = false }) => {
  // Set the document title
  useDocumentTitle('RCF Campaign Planner');
  
  const navigate = useNavigate();
  const toast = useCustomToast();
  const { user } = useAuth(); // Get the user from AuthContext

  // Add state for edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [campaignIdToEdit, setCampaignIdToEdit] = useState<string | null>(null);

  // Check for edit mode on component mount
  useEffect(() => {
    // Check if we have a campaign ID in the URL
    const params = new URLSearchParams(window.location.search);
    const editId = params.get('campaignId');
    
    if (editId) {
      setIsEditMode(true);
      setCampaignIdToEdit(editId);
      
      // Load campaign data for editing
      loadCampaignData(editId);
    }
  }, []);

  // Function to load campaign data for editing
  const loadCampaignData = async (campaignId: string) => {
    try {
      console.log(`Loading campaign data for ID: ${campaignId}`);
      
      // Fetch the campaign data
      const campaign = await getRCFCampaignById(campaignId);
      
      if (campaign && campaign.rcf_data) {
        // Set form state from campaign data
        if (campaign.rcf_data.offer_creation) {
          setOfferCreation(campaign.rcf_data.offer_creation);
        }
        
        if (campaign.rcf_data.loe_calculator) {
          setLoeCalculator(campaign.rcf_data.loe_calculator);
        }
        
        if (campaign.rcf_data.sequence_mapping) {
          const { items, groups, ...sequenceMappingData } = campaign.rcf_data.sequence_mapping;
          
          // Set the sequence mapping form fields
          setSequenceMapping(sequenceMappingData);
          
          // Set items and groups if they exist
          if (items) {
            setItems(items);
          }
          
          if (groups) {
            setGroups(groups);
          }
        }
        
        if (campaign.rcf_data.contextual_value_angles) {
          setContextualValueAngles(campaign.rcf_data.contextual_value_angles);
        }
        
        // Show success message
        toast({
          title: 'Campaign Loaded',
          description: 'Campaign data loaded successfully for editing',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error('Invalid campaign data format');
      }
    } catch (error) {
      console.error('Error loading campaign data:', error);
      toast({
        title: 'Error Loading Campaign',
        description: `Failed to load campaign data: ${error instanceof Error ? error.message : 'Unknown error'}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Function to initialize the email assignments array for all content emails
  const initializeEmailAssignments = (count: number): Record<number, string[]> => {
    const assignments: Record<number, string[]> = {};
    
    // Create an empty array for each email index
    for (let i = 1; i <= count; i++) {
      assignments[i] = [];
    }
    
    return assignments;
  };

  // Function to toggle section expansion
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // State for tracking which sections are expanded/collapsed
  const [expandedSections, setExpandedSections] = useState({
    offerCreation: false,
    loeCalculator: false,
    sequenceMapping: false,
    contextualValueAngles: false
  });

  // Form state for Offer Creation section
  const [offerCreation, setOfferCreation] = useState<OfferCreationState>({
    campaignName: '',
    objective: '',
    loeChoice: null as any,
    offerIngredients: [],
    startDate: new Date() > new Date('2025-01-01') ? new Date() : new Date('2025-01-01') // Use today or Jan 2025, whichever is later
  });
  
  // Form state for LOE Calculator section
  const [loeCalculator, setLoeCalculator] = useState<LOECalculatorState>({
    emailClicks: '',
    conversionRate: '',
    averageOrderValue: '',
    unitCost: '',
    hardCosts: '',
    personnelCosts: '',
    daysToImplement: '',
    relationshipScore: 1, // Default to 'Ok'
    opportunityCostPreset: 'Medium' // Default to Medium
  });

  // Form state for Sequence Mapping section
  const [sequenceMapping, setSequenceMapping] = useState<SequenceMappingState>({
    productName: '',
    productPrice: '',
    uniqueMechanism: '',
    resultTheyWant: '',
    mainBelief: '',
    reasonForPromo: '',
    subObjective: 'Revenue' // Default to Revenue
  });

  // State for Contextual Value Angles section - with proper initialization
  const [contextualValueAngles, setContextualValueAngles] = useState<ContextualValueAnglesState>(() => {
    return {
      contentEmailCount: NaN, // Start with an empty input (NaN represents empty)
      emailAssignments: {}
    };
  });
  
  // New angle input state
  const [newAngle, setNewAngle] = useState('');
  const [isAddingAngle, setIsAddingAngle] = useState(false);
  const [addAngleError, setAddAngleError] = useState('');
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  
  // Predefined contextual value angles
  const predefinedAngles = [
    'Unique Mechanism of Product', 
    'Industry Data', 
    'Industry Trends', 
    'Industry News', 
    '3rd Party Stories', 
    'Customer Stories', 
    'Business (Yours) Stories', 
    'Personal Stories', 
    'Case Studies', 
    'Tips', 
    'Best Practices', 
    'Little Known Facts', 
    'Contrarian Point of View', 
    'What to Avoid', 
    'How to', 
    'Problem Identification', 
    'Little Known Solutions', 
    'AMA (Ask Me Anything)', 
    'FAQ (Frequently Asked Questions)', 
    'Holidays', 
    'Major Events', 
    'Milestones'
  ];
  
  // State for custom angles
  const [customAngles, setCustomAngles] = useState<string[]>([]);
  
  // Combine predefined and custom angles
  const allAngles = [...predefinedAngles, ...customAngles];
  
  // Unified state for managing items and groups
  const [items, setItems] = useState<Record<string, Item>>({});
  const [groups, setGroups] = useState<Record<string, Group>>({});
  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  const [ungroupedObjections, setUngroupedObjections] = useState<string[]>([]);
  const [ungroupedBenefits, setUngroupedBenefits] = useState<string[]>([]);

  // Text area input states
  const [objectionsText, setObjectionsText] = useState('');
  const [benefitsText, setBenefitsText] = useState('');
  
  // Selected items for grouping
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>({});
  

  // Relationship score options with visible labels and hidden values
  const relationshipScoreOptions = [
    { label: 'Bad', value: 0.25 },
    { label: 'Ok', value: 1 },
    { label: 'Strong', value: 2 },
    { label: 'Evangelist', value: 3 },
  ];
  
  // Opportunity cost preset options
  const opportunityCostOptions = ['Low', 'Medium', 'High'];
  
  // Opportunity cost factors (not displayed to users)
  const opportunityCostFactors: Record<string, number> = {
    Low: 50,
    Medium: 20,
    High: 10
  };

  // LOE category thresholds for each opportunity cost preset
  const loeCategoryThresholds: Record<string, {low: number, medium: number}> = {
    Low: {
      low: 1000,    // 0-1000 = Low
      medium: 3000  // 1000-3000 = Medium, 3000+ = High
    },
    Medium: {
      low: 1500,    // 0-1500 = Low
      medium: 4500  // 1500-4500 = Medium, 4500+ = High
    },
    High: {
      low: 2000,    // 0-2000 = Low
      medium: 6000  // 2000-6000 = Medium, 6000+ = High
    }
  };

  // State for custom ingredients
  const [customIngredients, setCustomIngredients] = useState<string[]>([]);
  const [newIngredient, setNewIngredient] = useState('');
  const [isAddingIngredient, setIsAddingIngredient] = useState(false);
  const [addIngredientError, setAddIngredientError] = useState('');

  // Predefined offer ingredients
  const predefinedIngredients = [
    'Free Trial or Sample', 
    'Discount on Existing Products', 
    '2 for 1 Deal', 
    'Loyalty Points', 
    'Bundle Multiple Products', 
    'Launch New Product with Early Bird Pricing', 
    'Limited Quantity or VIP Access', 
    'Launch New Product with limited quantity', 
    'Free Gift or Bonus with Purchase', 
    'Beta Early Access', 
    'Dime Sale', 
    'Tiered Quantity Increase', 
    'Percentage Donation Each Sale', 
    'Mystery Box', 
    'Pay What You Want'
  ];
  
  // Combine predefined and custom ingredients
  const allIngredients = [...predefinedIngredients, ...customIngredients];
  
  // Validation state for all form sections
  const [validationState, setValidationState] = useState<ValidationState>({
    offerCreation: {
      campaignName: false,
      clearObjective: false,
      selectedLOE: false, // No default selected
      atLeastOneIngredient: false,
      hasStartDate: true, // Default to true since we're providing a default date
      isValid: false
    },
    loeCalculator: {
      emailClicks: false,
      conversionRate: false,
      averageOrderValue: false,
      hardCosts: false,
      personnelCosts: false, 
      daysToImplement: false,
      relationshipScore: true, // Default to true since we have a default value
      opportunityCostPreset: true, // Default to true since we have a default value
      isValid: false
    },
    sequenceMapping: {
      productName: false,
      productPrice: false,
      uniqueMechanism: false,
      resultTheyWant: false,
      mainBelief: false,
      reasonForPromo: false,
      subObjective: false,
      hasAtLeastOneGroup: false,
      isValid: false
    },
    contextualValueAngles: {
      contentEmailCount: true, // Always true now that we allow zero as a valid value
      hasAtLeastOneAngleAssigned: true, // Default to true when contentEmailCount is 0
      isValid: true // Default to true since both contentEmailCount and hasAtLeastOneAngleAssigned are true
    },
    allValid: false
  });

  // Track initial state for unsaved changes detection
  const [initialState, setInitialState] = useState<FormState | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Fetch custom ingredients on component mount
  useEffect(() => {
    fetchCustomIngredients();
  }, []);
  
  // Initialize initial state for unsaved changes detection
  useEffect(() => {
    setInitialState({
      offerCreation,
      loeCalculator,
      sequenceMapping,
      contextualValueAngles,
      groups,
      items
    });
  }, []);

  // Helper function to check if a value has changed
  const hasValueChanged = (current: any, initial: any): boolean => {
    // Handle null or undefined
    if (current === initial) return false;
    if (current === null || initial === null) return true;
    if (current === undefined || initial === undefined) return true;
    
    // Handle primitive types
    if (typeof current !== 'object' && typeof initial !== 'object') {
      return current !== initial;
    }
    
    // Handle arrays
    if (Array.isArray(current) && Array.isArray(initial)) {
      if (current.length !== initial.length) return true;
      return current.some((value, index) => hasValueChanged(value, initial[index]));
    }
    
    // Handle objects
    if (typeof current === 'object' && typeof initial === 'object') {
      const currentKeys = Object.keys(current);
      const initialKeys = Object.keys(initial);
      
      // If different number of keys, something changed
      if (currentKeys.length !== initialKeys.length) return true;
      
      // Check if any key is different
      return currentKeys.some(key => hasValueChanged(current[key], initial[key]));
    }
    
    return true;
  };

  // Check for unsaved changes
  useEffect(() => {
    if (initialState) {
      const currentState: FormState = {
        offerCreation,
        loeCalculator,
        sequenceMapping,
        contextualValueAngles,
        groups,
        items
      };
      
      // More robust change detection with actual deep comparison
      const changed = hasValueChanged(currentState, initialState);
      setHasUnsavedChanges(changed);
    }
  }, [offerCreation, loeCalculator, sequenceMapping, contextualValueAngles, groups, items, initialState]);

  // Setup beforeunload event listener for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Flag to check if we have valid inputs
  const hasBasicValidation = loeCalculator.emailClicks.trim() !== '' &&
    loeCalculator.conversionRate.trim() !== '' &&
    loeCalculator.averageOrderValue.trim() !== '' &&
    loeCalculator.hardCosts.trim() !== '' &&
    loeCalculator.personnelCosts.trim() !== '' &&
    loeCalculator.daysToImplement.trim() !== '' &&
    loeCalculator.relationshipScore > 0 &&
    loeCalculator.opportunityCostPreset !== undefined;
  
  // State for calculated results
  const [calculatedResults, setCalculatedResults] = useState<CalculatedResultsState>({
    revenueTotal: 0,
    loeValue: 0,
    marketingProfit: 0,
    hasValidInputs: false,
    loeCategory: 'Medium'
  });
  
  // Update calculated results when inputs change - use the server API
  useEffect(() => {
    // Skip calculation if inputs are not valid
    if (!hasBasicValidation) {
      setCalculatedResults({
        revenueTotal: 0,
        loeValue: 0,
        marketingProfit: 0,
        hasValidInputs: false,
        loeCategory: 'Medium'
      });
      return;
    }
    
    // Set loading state if needed
    // setIsLoading(true);
    
    // Call the remote calculation API
    const fetchCalculation = async () => {
      const result = await calculateLOERemote({
        emailClicks: loeCalculator.emailClicks,
        conversionRate: loeCalculator.conversionRate,
        averageOrderValue: loeCalculator.averageOrderValue,
        hardCosts: loeCalculator.hardCosts,
        personnelCosts: loeCalculator.personnelCosts,
        daysToImplement: loeCalculator.daysToImplement,
        relationshipScore: loeCalculator.relationshipScore,
        opportunityCostPreset: loeCalculator.opportunityCostPreset || 'Medium'
      });
      
      // Update state with remote calculation results
      setCalculatedResults({
        revenueTotal: result.revenueTotal,
        loeValue: result.loeValue,
        marketingProfit: result.marketingProfit,
        hasValidInputs: result.hasValidInputs,
        loeCategory: result.loeCategory
      });
      
      // Clear loading state if needed
      // setIsLoading(false);
    };
    
    fetchCalculation();
  }, [loeCalculator]);

  // Function to validate if at least one item exists in any group
  const validateAtLeastOneItemInGroups = (): boolean => {
    // Get all grouped item IDs
    const groupedItemIds = Object.values(groups).flatMap(group => group.itemIds);
    
    // Check if there's at least one item in any group
    // Users are not required to assign all items, just at least one
    return groupedItemIds.length > 0;
  };

  // Add a state for tracking creation phases
  const [creationPhase, setCreationPhase] = useState<'idle' | 'preparing' | 'content' | 'promotion' | 'complete'>('idle');
  const [creationProgress, setCreationProgress] = useState<number>(0);
  const [creationError, setCreationError] = useState<{
    content?: string;
    promotion?: string;
    general?: string;
  } | null>(null);
  const [contentCampaignId, setContentCampaignId] = useState<string | null>(null);

  // Add a state for action options after creation
  const [showPostCreationOptions, setShowPostCreationOptions] = useState<boolean>(false);

  // Add validation logic for content email angles specifically
  const validateContentEmailAngles = (): boolean => {
    // If content email count is 0, validation automatically passes
    if (contextualValueAngles.contentEmailCount === 0) {
      return true;
    }
    
    // Check each email has at least one angle assigned
    for (let emailNum = 1; emailNum <= contextualValueAngles.contentEmailCount; emailNum++) {
      if (!contextualValueAngles.emailAssignments[emailNum] || 
          contextualValueAngles.emailAssignments[emailNum].length === 0) {
        return false;
      }
    }
    return true;
  };

  // Enhance validation for sequence mapping to only check that at least one group has at least one item
  const validatePromotionGroups = (): boolean => {
    // If no groups exist, validation fails
    if (Object.keys(groups).length === 0) {
      return false;
    }
    
    // At least one group exists, so check if it contains at least one item
    return validateAtLeastOneItemInGroups();
  };

  // Update the validateForm function with clearer variable naming
  const validateForm = (): ValidationState => {
    // Validate offer creation step
    const offerValid = {
      campaignName: offerCreation.campaignName.trim().length > 0,
      clearObjective: offerCreation.objective.trim().length > 0,
      selectedLOE: !!offerCreation.loeChoice,
      atLeastOneIngredient: offerCreation.offerIngredients.length > 0,
      hasStartDate: !!offerCreation.startDate,
      isValid: false
    };
    offerValid.isValid = offerValid.campaignName && offerValid.clearObjective && offerValid.selectedLOE && offerValid.atLeastOneIngredient && offerValid.hasStartDate;
    
    // Validate LOE calculator step - only if expanded
    const loeValid = {
      emailClicks: loeCalculator.emailClicks.trim().length > 0,
      conversionRate: loeCalculator.conversionRate.trim().length > 0,
      averageOrderValue: loeCalculator.averageOrderValue.trim().length > 0,
      hardCosts: loeCalculator.hardCosts.trim().length > 0,
      personnelCosts: loeCalculator.personnelCosts.trim().length > 0,
      daysToImplement: loeCalculator.daysToImplement.trim().length > 0,
      relationshipScore: loeCalculator.relationshipScore > 0,
      opportunityCostPreset: !!loeCalculator.opportunityCostPreset,
      isValid: false
    };
    
    if (expandedSections.loeCalculator) {
      loeValid.isValid = loeValid.emailClicks && 
                         loeValid.conversionRate && 
                         loeValid.averageOrderValue && 
                         loeValid.hardCosts && 
                         loeValid.personnelCosts && 
                         loeValid.daysToImplement && 
                         loeValid.relationshipScore && 
                         loeValid.opportunityCostPreset;
    } else {
      loeValid.isValid = true; // Skip validation if not expanded
    }
    
    // Validate sequence mapping step
    const sequenceValid = {
      productName: sequenceMapping.productName.trim().length > 0,
      productPrice: sequenceMapping.productPrice.trim().length > 0,
      uniqueMechanism: sequenceMapping.uniqueMechanism.trim().length > 0,
      resultTheyWant: sequenceMapping.resultTheyWant.trim().length > 0,
      mainBelief: sequenceMapping.mainBelief.trim().length > 0,
      reasonForPromo: sequenceMapping.reasonForPromo.trim().length > 0,
      subObjective: !!sequenceMapping.subObjective,
      hasAtLeastOneGroup: false,
      isValid: false
    };
    
    const hasAtLeastOneGroupWithItems = validatePromotionGroups();
    sequenceValid.hasAtLeastOneGroup = hasAtLeastOneGroupWithItems;
    
    sequenceValid.isValid = sequenceValid.productName && 
                           sequenceValid.productPrice && 
                           sequenceValid.uniqueMechanism && 
                           sequenceValid.resultTheyWant && 
                           sequenceValid.mainBelief && 
                           sequenceValid.reasonForPromo && 
                           sequenceValid.subObjective &&
                           sequenceValid.hasAtLeastOneGroup;
    
    const contentEmailCountValid = contextualValueAngles.contentEmailCount !== null && 
                                 contextualValueAngles.contentEmailCount !== undefined && 
                                 !isNaN(contextualValueAngles.contentEmailCount) &&
                                 contextualValueAngles.contentEmailCount >= 0;
    
    const contentAnglesValid = validateContentEmailAngles();
    
    const anglesValid = {
      contentEmailCount: contentEmailCountValid,
      hasAtLeastOneAngleAssigned: contentAnglesValid,
      isValid: false
    };
    
    if (contentEmailCountValid && contextualValueAngles.contentEmailCount === 0) {
      anglesValid.hasAtLeastOneAngleAssigned = true; 
      anglesValid.isValid = true;
    } else {
      anglesValid.isValid = anglesValid.contentEmailCount && anglesValid.hasAtLeastOneAngleAssigned;
    }
    
    const hasDateError = campaignOverlapError || dateSelectionOverlapError;

    // --- ADDED DEBUG LOGGING ---
    console.log('[ValidateForm Debug]', {
      offerIsValid: offerValid.isValid,
      loeIsValid: loeValid.isValid,
      sequenceIsValid: sequenceValid.isValid,
      anglesIsValid: anglesValid.isValid,
      hasDateError: !!hasDateError, // Ensure boolean
      campaignOverlapError: campaignOverlapError,
      dateSelectionOverlapError: dateSelectionOverlapError
    });
    // --- END DEBUG LOGGING ---
    
    const allValid = offerValid.isValid && 
                    loeValid.isValid && 
                    sequenceValid.isValid && 
                    anglesValid.isValid &&
                    !hasDateError; 
    
    return {
      offerCreation: offerValid,
      loeCalculator: loeValid,
      sequenceMapping: sequenceValid,
      contextualValueAngles: anglesValid,
      allValid
    };
  };

  // Run form validation when relevant state changes
  useEffect(() => {
    // Wrap in a requestAnimationFrame to prevent rapid cascading updates
    const validateFormAndUpdateState = () => {
      const newValidationState = validateForm();
      setValidationState(newValidationState);
    };
    
    const timeoutId = setTimeout(validateFormAndUpdateState, 10);
    return () => clearTimeout(timeoutId);
  }, [offerCreation, loeCalculator, sequenceMapping, contextualValueAngles, Object.keys(groups).length]);

  // Format the promotion groups for the campaign data
  const formatPromotionGroups = () => {
    // Format the promotion groups for the rcf_data structure
    return Object.entries(groups).map(([groupId, group]) => {
      return {
        day: parseInt(groupId.replace('group-', ''), 10),
        items: group.itemIds.map(itemId => ({
          id: itemId,
          type: items[itemId].type,
          content: items[itemId].content
        }))
      };
    }).sort((a, b) => a.day - b.day); // Sort by day number
  };

  // Generate comprehensive campaign notes with proper HTML formatting
  const generateCampaignNotes = () => {
    // Get all selected offer ingredients
      const selectedIngredients = offerCreation.offerIngredients.join(', ');
      
      // Format the general campaign information section with HTML bold tags
      const generalInfo = `<strong>Campaign Information</strong><br><br>

Campaign Objective: ${offerCreation.objective}<br>
Offer Ingredients: ${selectedIngredients}<br><br>

Product Name: ${sequenceMapping.productName}<br>
Product Price: ${formatCurrency(parseFloat(sequenceMapping.productPrice) || 0)}<br>
Unique Mechanism: ${sequenceMapping.uniqueMechanism}<br>
Result They Want & Conditions They Want: ${sequenceMapping.resultTheyWant}<br>
Main Belief To Purchase: ${sequenceMapping.mainBelief}<br>
Reason For Promo (Theme/Angle): ${sequenceMapping.reasonForPromo}<br>
Promotion Objective: ${sequenceMapping.subObjective}<br>`;
      
      // Format content phase details with HTML bold tags
      const contentPhaseDetails = `<strong>Content Phase Details</strong><br><br>

Total Content Days: ${contextualValueAngles.contentEmailCount}<br>
${Object.entries(contextualValueAngles.emailAssignments).map(([day, angles]) => 
  `Content Day ${day}: ${angles.join(', ')}<br>`
).join('')}`;
      
      // Format promotion phase details with HTML bold tags
      const promotionPhaseDetails = `<strong>Promotion Phase Details</strong><br><br>

Total Promotion Days: ${Object.keys(groups).length}<br>
${Object.entries(groups).map(([groupId, group], index) => {
  const dayNumber = index + 1;
  const groupItems = group.itemIds.map(itemId => items[itemId]?.content || '').filter(Boolean);
  return `Promotion Day ${dayNumber}: ${groupItems.join(', ')}<br>`;
}).join('')}`;
      
      // Return with proper spacing between sections
      return `${generalInfo}<br>
${contentPhaseDetails}<br>
${promotionPhaseDetails}<br>

Level of Effort: ${offerCreation.loeChoice} (calculated: ${calculatedResults.loeCategory})<br>
Total Campaign Duration: ${contextualValueAngles.contentEmailCount + Object.keys(groups).length} days<br><br>

Note: This is an Repeat Customer Formula campaign with content phase followed by promotion phase.`;
  };

  // Calculate total campaign duration
  const calculateCampaignDuration = () => {
    // Calculate total campaign duration (content emails + promotion groups)
    return contextualValueAngles.contentEmailCount + Object.keys(groups).length;
  };

  // Add state for existing campaigns and campaign overlap error
  const [existingCampaigns, setExistingCampaigns] = useState<Campaign[]>([]);
  const [campaignOverlapError, setCampaignOverlapError] = useState<string | null>(null);
  // Add state for debug mode

  const [dateSelectionOverlapError, setDateSelectionOverlapError] = useState<string | null>(null);

  // Add a function to fetch existing campaigns with silent retries
  const fetchExistingCampaigns = useCallback(async (retryCount = 0) => {
    try {
      // Get the timezone from user preferences or fall back to browser default
      const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Get campaigns using the campaign service
      const apiCampaigns = await getCampaigns(timezone);
      
      console.log('Campaigns from API:', { 
        count: apiCampaigns?.length || 0,
        campaignTypes: [...new Set(apiCampaigns.map(c => c.type))]
      });
      
      // Use campaigns from API
      if (apiCampaigns && apiCampaigns.length > 0) {
        setExistingCampaigns(apiCampaigns);
      } else {
        console.warn('No campaigns found from API');
        setExistingCampaigns([]);
      }
    } catch (error) {
      console.error(`Error fetching existing campaigns (attempt ${retryCount + 1}):`, error);
      setExistingCampaigns([]);
      
      // Retry up to 2 times with increasing delay
      if (retryCount < 2) {
        const delay = 2000 * (retryCount + 1); // 2s, 4s
        setTimeout(() => {
          fetchExistingCampaigns(retryCount + 1);
        }, delay);
      }
    }
  }, [user?.timezone]); // Add user?.timezone as a dependency so it refetches when timezone changes

  // Add useEffect to fetch campaigns on component mount
  useEffect(() => {
    fetchExistingCampaigns();
  }, [fetchExistingCampaigns]);

  // Add useEffect to refetch campaigns when timezone changes
  useEffect(() => {
    // Only refetch if we have a user and timezone
    if (user?.timezone) {
      console.log('User timezone changed, refetching campaigns:', user.timezone);
      fetchExistingCampaigns();
    }
  }, [user?.timezone, fetchExistingCampaigns]);



  // Improved function to check for campaign date overlaps with better validation and logging
  const checkForCampaignOverlap = (
    campaignsToCheckOrStartDate: any[] | Date,
    startDateOrEndDate?: Date,
    startTime?: string | undefined,
    timezone?: string,
    windowInDays = 7,
    checkExactDateOnly?: boolean // Added new parameter to control single-day checking
  ) => {
    // Handle both function signatures - old and new
    let startDate: Date;
    let allCampaigns: any[];
    
    // Check if called with old signature: (startDate, endDate)
    if (campaignsToCheckOrStartDate instanceof Date) {
      startDate = campaignsToCheckOrStartDate;
      
      // Use all campaigns from merged sources for checking
      // Look for allCampaigns first, then fall back to empty array
      allCampaigns = existingCampaigns || [];
      
      console.log(`Overlap check for date range: ${startDate} to ${startDateOrEndDate}, exact date only: ${checkExactDateOnly}`);
      
      // If we're checking only the exact date (for inline messages)
      if (checkExactDateOnly) {
        return allCampaigns.some(campaign => {
          try {
            // Handle both property naming conventions (startDate vs start_date)
            const campaignStart = new Date(campaign.startDate || campaign.start_date);
            const campaignEnd = campaign.endDate 
              ? new Date(campaign.endDate) 
              : campaign.end_date 
                ? new Date(campaign.end_date) 
                : add(campaignStart, { days: 1 });
            
            // Reset hours for accurate date comparison
            campaignStart.setHours(0, 0, 0, 0);
            campaignEnd.setHours(0, 0, 0, 0);
            startDate.setHours(0, 0, 0, 0);
            
            // Check if this exact date falls within any campaign
            return isWithinInterval(startDate, { 
              start: campaignStart, 
              end: campaignEnd 
            }) || isSameDay(startDate, campaignStart) || isSameDay(startDate, campaignEnd);
          } catch (e) {
            console.error(`Error checking overlap for campaign ${campaign.id}:`, e);
            return false;
          }
        });
      }
      
      // In the old signature, we don't use timezone, windowInDays, etc.
      // The second parameter was the end date
      
      // Simple check - see if any campaign overlaps with the given date range
      return allCampaigns.some(campaign => {
        try {
          // Handle both property naming conventions (startDate vs start_date)
          const campaignStart = new Date(campaign.startDate || campaign.start_date);
          const campaignEnd = campaign.endDate 
            ? new Date(campaign.endDate) 
            : campaign.end_date 
              ? new Date(campaign.end_date) 
              : add(campaignStart, { days: 1 });
          
          // Reset hours for accurate date comparison
          campaignStart.setHours(0, 0, 0, 0);
          campaignEnd.setHours(0, 0, 0, 0);
          startDate.setHours(0, 0, 0, 0);
          const endDate = startDateOrEndDate ? new Date(startDateOrEndDate) : startDate;
          endDate.setHours(0, 0, 0, 0);
          
          // Check for date overlap
          return (
            isWithinInterval(startDate, { start: campaignStart, end: campaignEnd }) ||
            isWithinInterval(endDate, { start: campaignStart, end: campaignEnd }) ||
            isWithinInterval(campaignStart, { start: startDate, end: endDate }) ||
            isWithinInterval(campaignEnd, { start: startDate, end: endDate }) ||
            (isSameDay(startDate, campaignStart) && isSameDay(endDate, campaignEnd))
          );
        } catch (e) {
          console.error(`Error checking overlap for campaign ${campaign.id}:`, e);
          return false;
        }
      });
    }
    
    // Rest of the function remains the same
    // If called with new signature: (campaignsToCheck[], startDate, startTime?, timezone?, windowInDays?)
    // ...existing code...
  };

  // More accurate single date check that doesn't flag the next day after an existing campaign
  const checkSingleDateOverlap = (date: Date): boolean => {
    const dateCopy = new Date(date);
    dateCopy.setHours(0, 0, 0, 0);
    
    return existingCampaigns.some(campaign => {
      try {
        const campaignStart = new Date(campaign.startDate);
        const campaignEnd = new Date(campaign.endDate);
        
        // Reset hours for accurate date comparison
        campaignStart.setHours(0, 0, 0, 0);
        campaignEnd.setHours(0, 0, 0, 0);
        
        // Check if this exact date falls within the campaign's range
        // But exclude the case where dateCopy is the day after campaignEnd
        return (dateCopy > campaignStart && dateCopy <= campaignEnd) || 
               isSameDay(dateCopy, campaignStart);
      } catch (e) {
        console.error(`Error checking date overlap:`, e);
        return false;
      }
    });
  };

  // Use validateCampaignDuration for full campaign duration checking
  const validateCampaignDuration = useCallback(() => {
    if (!offerCreation.startDate) return false;
    
    // Calculate campaign end date based on current duration
    const campaignDuration = calculateCampaignDuration();
    const endDate = new Date(offerCreation.startDate.getTime() + (campaignDuration * 24 * 60 * 60 * 1000));
    
    // Check for overlaps with existing campaigns - use the full duration
    return checkForCampaignOverlap(offerCreation.startDate, endDate);
  }, [offerCreation.startDate, calculateCampaignDuration, checkForCampaignOverlap]);

  // Add a date change handler if it doesn't exist already
  const handleStartDateChange = (date: Date) => {
    // First update the state with new date
    setOfferCreation(prev => ({
      ...prev,
      startDate: date
    }));
    
    // Log information about the selected date and existing campaigns
    console.log('Start date changed:', {
      date: date.toISOString(),
      formattedDate: date.toLocaleDateString(),
      timezone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    
    // Check ONLY the exact date for immediate feedback - not a window
    const hasExactDateOverlap = checkSingleDateOverlap(date);
    
    if (hasExactDateOverlap) {
      // Find which specific campaigns overlap with this exact date
      const overlappingCampaigns = existingCampaigns.filter(campaign => {
        try {
          const campaignStart = new Date(campaign.startDate);
          const campaignEnd = new Date(campaign.endDate);
          
          // Reset hours for accurate date comparison
          campaignStart.setHours(0, 0, 0, 0);
          campaignEnd.setHours(0, 0, 0, 0);
          
          // Check if this exact date falls within the campaign
          const dateCopy = new Date(date);
          dateCopy.setHours(0, 0, 0, 0);
          
          // Use more accurate overlap logic that doesn't flag day after campaign end
          return (dateCopy > campaignStart && dateCopy <= campaignEnd) || 
                 isSameDay(dateCopy, campaignStart);
        } catch (e) {
          return false;
        }
      });
      
      if (overlappingCampaigns.length > 0) {
        let errorMessage = 'This date already has scheduled campaign(s):';
        overlappingCampaigns.forEach(campaign => {
          const campaignStart = new Date(campaign.startDate);
          const campaignEnd = new Date(campaign.endDate);
          errorMessage += `\n• "${campaign.name}" (${campaignStart.toLocaleDateString()} - ${campaignEnd.toLocaleDateString()})`;
        });
        errorMessage += '\n\nPlease choose a different date.';
        setDateSelectionOverlapError(errorMessage);
      } else {
        // Fallback if we can't determine which campaigns overlap
        setDateSelectionOverlapError(
          `This date already has a scheduled campaign. Please choose a different date.`
        );
      }
    } else {
      setDateSelectionOverlapError(null);
    }
    
    // Also check for campaign duration overlaps (handles the footer message)
    const hasFullDurationOverlap = validateCampaignDuration();
    if (hasFullDurationOverlap) {
      const campaignDuration = calculateCampaignDuration();
      const endDate = new Date(date.getTime() + (campaignDuration * 24 * 60 * 60 * 1000));
      setCampaignOverlapError(
        `This campaign (${date.toLocaleDateString()} - ${endDate.toLocaleDateString()}) overlaps with an existing campaign. Please choose a different start date.`
      );
    } else {
      setCampaignOverlapError(null);
    }
  };

  // Use effect to run date overlap check on initial mount with today's date
  useEffect(() => {
    // Check today's date for overlaps when component mounts
    if (offerCreation.startDate && existingCampaigns.length > 0) {
      // Ensure the date is within the valid range (not before today/Jan 2025 and not more than 13 months ahead)
      const today = new Date();
      const minDate = today > new Date('2025-01-01') ? today : new Date('2025-01-01');
      const maxDate = new Date(new Date().setMonth(new Date().getMonth() + 13));
      
      let dateToUse = offerCreation.startDate;
      
      // If date is before min date, use min date
      if (dateToUse < minDate) {
        dateToUse = minDate;
        setOfferCreation(prev => ({
          ...prev,
          startDate: dateToUse
        }));
      }
      
      // If date is after max date, use max date
      if (dateToUse > maxDate) {
        dateToUse = maxDate;
        setOfferCreation(prev => ({
          ...prev,
          startDate: dateToUse
        }));
      }
      
      handleStartDateChange(dateToUse);
    }
  }, [existingCampaigns]);  // <- Add existingCampaigns as a dependency

  // Use effect to validate campaign duration whenever relevant state changes
  useEffect(() => {
    // Only check if we have a start date
    if (offerCreation.startDate) {
      const hasOverlap = validateCampaignDuration();
      if (hasOverlap) {
        const campaignDuration = calculateCampaignDuration();
        const endDate = new Date(offerCreation.startDate.getTime() + (campaignDuration * 24 * 60 * 60 * 1000));
        setCampaignOverlapError(
          `This campaign (${offerCreation.startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}) overlaps with an existing campaign. Please choose a different start date.`
        );
      } else {
        setCampaignOverlapError(null);
      }
    }
  }, [offerCreation.startDate, contextualValueAngles.contentEmailCount, Object.keys(groups).length, validateCampaignDuration, calculateCampaignDuration]);

  // Function to create campaign when button is clicked
  const handleCreateCampaign = async () => {
    try {
      // Reset any previous errors and set initial states
      setIsCreatingCampaign(true);
      setCreationPhase('idle');
      setCreationProgress(0);
      setCreationError(null);
      setContentCampaignId(null);
      setShowPostCreationOptions(false);
      setCampaignOverlapError(null); // Reset overlap error
      
      // Animation delay for progress bar steps
      const progressDelay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
      
      // Use the selected start date - ensure it's defined
      const startDate = offerCreation.startDate || new Date();
      
      // Start progress
      await progressDelay(300);
      setCreationProgress(10);
      setCreationPhase('preparing');
      
      // Calculate end date based on campaign duration
      const campaignDuration = calculateCampaignDuration();
      // Subtract 1 day from duration since the date range is inclusive (e.g., 4 day campaign = start date + 3 days)
      // This prevents the calendar from showing an extra promotion day
      const endDate = new Date(startDate.getTime() + ((campaignDuration - 1) * 24 * 60 * 60 * 1000));
      
      console.log('Checking campaign dates for overlap:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        campaignDuration,
        existingCampaigns: existingCampaigns?.length || 0
      });
      
      // Check for campaign overlaps
      const hasOverlap = checkForCampaignOverlap(startDate, endDate);
      if (hasOverlap) {
        // Find which campaign(s) it overlaps with for a more helpful message
        const overlappingCampaigns = existingCampaigns.filter(campaign => {
          try {
            const campaignStart = new Date(campaign.startDate);
            const campaignEnd = new Date(campaign.endDate);
            
            // Reset hours for accurate date comparison
            campaignStart.setHours(0, 0, 0, 0);
            campaignEnd.setHours(0, 0, 0, 0);
            
            // Check for date overlap
            const startDateCopy = new Date(startDate);
            const endDateCopy = new Date(endDate);
            startDateCopy.setHours(0, 0, 0, 0);
            endDateCopy.setHours(0, 0, 0, 0);
            
            return (
              (startDateCopy <= campaignEnd && endDateCopy >= campaignStart)
            );
          } catch (e) {
            return false;
          }
        });
        
        let errorMessage = `This campaign (${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}) overlaps with existing campaign(s).`;
        
        if (overlappingCampaigns.length > 0) {
          errorMessage += ' Conflicts with:';
          overlappingCampaigns.forEach(campaign => {
            const campaignStart = new Date(campaign.startDate);
            const campaignEnd = new Date(campaign.endDate);
            errorMessage += `\n• "${campaign.name}" (${campaignStart.toLocaleDateString()} - ${campaignEnd.toLocaleDateString()})`;
          });
          errorMessage += '\n\nPlease choose a different start date.';
        } else {
          errorMessage += ' Please choose a different start date.';
        }
        
        console.error(errorMessage);
        setCampaignOverlapError(errorMessage);
        setIsCreatingCampaign(false);
        return; // Stop campaign creation
      }
      
      // Continue with campaign creation if no overlaps
      await progressDelay(300);
      setCreationPhase('content');
      setCreationProgress(25);
      
      // Format promotion groups in sequential order for the database
      const formattedGroups = formatPromotionGroups();
      
      // Generate campaign notes based on form data
      const campaignNotes = generateCampaignNotes();
      
      // Get the campaign name from user input or fall back to a default
      const campaignName = offerCreation.campaignName.trim() || 'New Campaign';
      
      // Get the timezone from user preferences instead of browser default
      const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Update progress with a slight delay to show transition
      await progressDelay(400);
      setCreationProgress(50);
      
      // Prepare the RCF campaign data
      const rcfCampaignData = {
        name: campaignName,
        startDate: startDate, // Use selected start date
        endDate: endDate,
        type: 'rcf_planner',
        notes: campaignNotes,
        // Add these values at the root level for the server
        content_days: contextualValueAngles.contentEmailCount,
        promotion_days: Object.keys(groups).length,
        rcf_data: {
          offer_creation: offerCreation,
          loe_calculator: loeCalculator,
          sequence_mapping: {
            ...sequenceMapping,
            items,
            groups
          },
          contextual_value_angles: contextualValueAngles,
          // Also include here for consistency
          content_days: contextualValueAngles.contentEmailCount,
          promotion_days: Object.keys(groups).length
        }
      };
      
      let result;
      
      // Update progress with a slight delay to show transition
      await progressDelay(400);
      setCreationProgress(75);
      setCreationPhase('promotion');
      
      if (isEditMode && campaignIdToEdit) {
        // Use update endpoint if in edit mode
        result = await updateRCFCampaign(campaignIdToEdit, rcfCampaignData, timezone);
        
        // Update progress to 100% and set phase to complete with a delay
        await progressDelay(400);
        setCreationProgress(100);
        setCreationPhase('complete');
        
        toast({
          title: 'Campaign Updated',
          description: `Repeat Customer Formula campaign "${rcfCampaignData.name}" has been updated successfully.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Reset unsaved changes flag since save was successful
        setHasUnsavedChanges(false);
        
        // Add a slight delay to show the completed progress bar before redirecting
        setTimeout(() => {
          // Use window.location.href instead of navigate to force a complete page reload
          window.location.href = '/dashboard';
        }, 2000);
      } else {
        // Use create endpoint for new campaigns
        result = await createRCFCampaign(rcfCampaignData, timezone);
        
        // Log the full response to understand its structure
        console.log('RCF Campaign creation response:', result);
        
        // Update progress to 100% with a delay to show the transition
        await progressDelay(400);
        setCreationProgress(100);
        setCreationPhase('complete');
        
        // If we have a successful result, show success and redirect
        if (result) {
          // The API might return either {id, ...} or {success: true, campaign: {id, ...}}
          const campaignId = result.id || (result.campaign && result.campaign.id);
          
          if (!campaignId) {
            console.error('Could not find campaign ID in response:', result);
            toast({
              title: 'Campaign Created',
              description: 'Your campaign was created successfully. Redirecting to dashboard...',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          } else {
            // Log content and promotion day values for debugging
            console.log('RCF campaign days breakdown:', {
              contentDays: contextualValueAngles.contentEmailCount,
              promotionDays: Object.keys(groups).length,
              totalDays: contextualValueAngles.contentEmailCount + Object.keys(groups).length
            });
            
            // Reset unsaved changes flag since save was successful
            setHasUnsavedChanges(false);
            
            // Show success toast
            toast({
              title: 'Campaign Created Successfully!',
              description: `Your "${rcfCampaignData.name}" campaign has been created.`,
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
          }
          
          // Show success state for a moment before redirecting
          await progressDelay(800);
          
          // Redirect to dashboard
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 1500);
        }
      }
    } catch (error: any) {
      console.error('Error creating campaign:', error);
      setCreationError(error.message || 'An unknown error occurred');
      setCreationPhase('idle'); // Set to a valid phase value
    } finally {
      setIsCreatingCampaign(false);
    }
  };
  
  // Fetch custom angles on component mount
  useEffect(() => {
    fetchCustomAngles();
    fetchCustomIngredients();
  }, []);
  
  // Function to fetch custom angles from the database
  const fetchCustomAngles = async () => {
    try {
      // Pass the user ID if available
      const angles = await getCustomAngles(user?.id);
      setCustomAngles(angles);
    } catch (error) {
      console.error('Error fetching custom angles:', error);
    }
  };

  // Function to fetch custom ingredients from the database
  const fetchCustomIngredients = async () => {
    try {
      // Pass the user ID if available
      const ingredients = await getCustomIngredients(user?.id);
      setCustomIngredients(ingredients);
    } catch (error) {
      console.error('Error fetching custom ingredients:', error);
    }
  };

  // Function to delete a custom ingredient
  const deleteIngredient = async (ingredient: string) => {
    try {
      // Pass the user ID if available
      const result = await deleteCustomIngredient(ingredient, user?.id);
      
      if (result.success) {
        // Remove from local state
        setCustomIngredients(prev => prev.filter(item => item !== ingredient));
        
        // Also remove from selected ingredients if it was selected
        if (offerCreation.offerIngredients.includes(ingredient)) {
          setOfferCreation(prev => ({
            ...prev,
            offerIngredients: prev.offerIngredients.filter(item => item !== ingredient)
          }));
        }
      }
    } catch (error) {
      console.error('Error deleting custom ingredient:', error);
    }
  };

  // Function to delete a custom angle
  const deleteAngle = async (angle: string) => {
    try {
      // Pass the user ID if available
      const result = await deleteCustomAngle(angle, user?.id);
      
      if (result.success) {
        // Remove from local state
        setCustomAngles(prev => prev.filter(item => item !== angle));
        
        // Also remove from any email assignments
        setContextualValueAngles(prev => {
          const newEmailAssignments = { ...prev.emailAssignments };
          
          // Remove the angle from all email assignments
          Object.keys(newEmailAssignments).forEach(emailNum => {
            newEmailAssignments[Number(emailNum)] = newEmailAssignments[Number(emailNum)].filter(
              item => item !== angle
            );
          });
          
          return {
            ...prev,
            emailAssignments: newEmailAssignments
          };
        });
      }
    } catch (error) {
      console.error('Error deleting custom angle:', error);
    }
  };

  /* Contextual Value Angles Section Logic */

  // Function to handle adding more content emails
  const handleAddContentEmail = () => {
    setContextualValueAngles(prev => {
      const newCount = prev.contentEmailCount + 1;
      const updatedAssignments = { ...prev.emailAssignments };
      updatedAssignments[newCount] = [];
      
      return {
        contentEmailCount: newCount,
        emailAssignments: updatedAssignments
      };
    });
  };

  // Function to handle removing the last content email
  const handleRemoveContentEmail = () => {
    setContextualValueAngles(prev => {
      if (prev.contentEmailCount <= 1) return prev;
      
      const newCount = prev.contentEmailCount - 1;
      const updatedAssignments = { ...prev.emailAssignments };
      delete updatedAssignments[prev.contentEmailCount];
      
      return {
        contentEmailCount: newCount,
        emailAssignments: updatedAssignments
      };
    });
  };

  // Handler for removing an item from a group and returning it to the ungrouped list
  const removeItemFromGroup = (groupId: string, itemId: string) => {
    // Get the item to determine its type
    const item = items[itemId];
    if (!item) return;
    
    // Remove the item from the group
    setGroups(prev => {
      const group = prev[groupId];
      if (!group) return prev;
      
      const updatedGroups = { ...prev };
      // Update the group by removing this item
      updatedGroups[groupId] = {
        ...group,
        itemIds: group.itemIds.filter(id => id !== itemId)
      };
      
      // If the group is now empty, remove it entirely
      if (updatedGroups[groupId].itemIds.length === 0) {
        delete updatedGroups[groupId];
        // Also update the group order
        setGroupOrder(prev => prev.filter(id => id !== groupId));
      }
      
      return updatedGroups;
    });
    
    // Add the item back to the appropriate ungrouped list
    if (item.type === 'objection') {
      setUngroupedObjections(prev => [...prev, itemId]);
    } else {
      setUngroupedBenefits(prev => [...prev, itemId]);
    }
  };

  // State for loading indicators
  // Removing duplicate state declarations - these are already declared at the top of the component
  // const [isAddingIngredient, setIsAddingIngredient] = useState<boolean>(false);
  // const [isAddingAngle, setIsAddingAngle] = useState<boolean>(false);
  // const [isCreatingCampaign, setIsCreatingCampaign] = useState<boolean>(false);

  // State for error messages
  // const [addIngredientError, setAddIngredientError] = useState<string>('');
  // const [addAngleError, setAddAngleError] = useState<string>('');

  // Handler for objective text input
  const handleObjectiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setOfferCreation(prev => ({ ...prev, objective: value }));
    setHasUnsavedChanges(true);
  };

  // Handler for campaign name text input
  const handleCampaignNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setOfferCreation(prev => ({ ...prev, campaignName: value }));
    setHasUnsavedChanges(true);
  };

  // Handler for LOE radio selection
  const handleLOEChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as 'Low' | 'Medium' | 'High';
    setOfferCreation(prev => ({ ...prev, loeChoice: value }));
    setHasUnsavedChanges(true);
  };

  // Handler for ingredient toggle checkboxes
  const handleIngredientToggle = (ingredient: string) => {
    setOfferCreation(prev => {
      // If ingredient is already selected, remove it
      if (prev.offerIngredients.includes(ingredient)) {
        return {
          ...prev,
          offerIngredients: prev.offerIngredients.filter(item => item !== ingredient)
        };
      } 
      // Otherwise, add it
      else {
        return {
          ...prev,
          offerIngredients: [...prev.offerIngredients, ingredient]
        };
      }
    });
  };

  // Handler for new custom ingredient input
  const handleNewIngredientChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewIngredient(e.target.value);
  };

  // Handler for key press events in the add ingredient input
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomIngredient();
    }
  };

  // Function to add a custom ingredient
  const addCustomIngredient = async () => {
    // Trim the input to remove whitespace
    const trimmedIngredient = newIngredient.trim();
    
    // Validate the input
    if (!trimmedIngredient) {
      setAddIngredientError('Please enter an ingredient name');
      return;
    }
    
    if (trimmedIngredient.length > 50) {
      setAddIngredientError('Ingredient name must be 50 characters or less');
      return;
    }
    
    if (allIngredients.includes(trimmedIngredient)) {
      setAddIngredientError('This ingredient already exists');
      return;
    }
    
    // Clear any previous errors
    setAddIngredientError('');
    
    // Set loading state
    setIsAddingIngredient(true);
    
    try {
      // Save the ingredient with the user ID
      const result = await saveCustomIngredient(trimmedIngredient, user?.id);
      
      if (result.success) {
        // Add to local state
        setCustomIngredients(prev => [...prev, trimmedIngredient]);
        
        // Clear the input
        setNewIngredient('');
        
        // Also select the new ingredient
        setOfferCreation(prev => ({
          ...prev,
          offerIngredients: [...prev.offerIngredients, trimmedIngredient]
        }));
      } else {
        setAddIngredientError(result.message);
      }
    } catch (error) {
      console.error('Error adding custom ingredient:', error);
      setAddIngredientError('Failed to add custom ingredient. Please try again.');
    } finally {
      setIsAddingIngredient(false);
    }
  };

  // Function to format currency values for display
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Handler for percentage input fields
  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    
    // Allow empty input for UX
    if (value === '') {
      setLoeCalculator(prev => ({
        ...prev,
        conversionRate: ''
      }));
      return;
    }
    
    // Only allow numbers and a single decimal point
    const regex = /^\d*\.?\d*$/;
    if (!regex.test(value)) return;
    
    // Parse the value and ensure it's within the range 0-100
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setLoeCalculator(prev => ({
        ...prev,
        conversionRate: value
      }));
    }
  };

  // Handler for currency input fields
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof LOECalculatorState) => {
    const { value } = e.target;
    
    // Allow empty input for UX
    if (value === '') {
      setLoeCalculator(prev => ({
        ...prev,
        [field]: ''
      }));
      return;
    }
    
    // Only allow numbers and a single decimal point
    const regex = /^\d*\.?\d*$/;
    if (!regex.test(value)) return;
    
    // Parse the value and ensure it's a valid number
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setLoeCalculator(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handler for positive integer input fields
  const handlePositiveIntegerChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof LOECalculatorState) => {
    const { value } = e.target;
    
    // Allow empty input for UX
    if (value === '') {
      setLoeCalculator(prev => ({
        ...prev,
        [field]: ''
      }));
      return;
    }
    
    // Only allow positive integers
    const regex = /^\d+$/;
    if (!regex.test(value)) return;
    
    // Parse the value and ensure it's a valid positive integer
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setLoeCalculator(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handler for relationship score dropdown
  const handleRelationshipScoreChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseFloat(e.target.value);
    setLoeCalculator(prev => ({
      ...prev,
      relationshipScore: value
    }));
  };

  // Handler for opportunity cost dropdown
  const handleOpportunityCostChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as 'Low' | 'Medium' | 'High';
    setLoeCalculator(prev => ({
      ...prev,
      opportunityCostPreset: value
    }));
  };

  // Handlers for Sequence Mapping section
  const handleSequenceTextChange = (
    e: React.ChangeEvent<HTMLInputElement>, 
    field: keyof SequenceMappingState, 
    maxLength: number
  ) => {
    const { value } = e.target;
    
    // Enforce max length
    if (value.length <= maxLength) {
      setSequenceMapping(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Specific handler for product price as it requires currency formatting
  const handleProductPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    
    // Allow empty input
    if (value === '') {
      setSequenceMapping(prev => ({
        ...prev,
        productPrice: ''
      }));
      return;
    }
    
    // Only allow numbers and a single decimal point
    const regex = /^\d*\.?\d*$/;
    if (!regex.test(value)) return;
    
    // Parse the value and ensure it's a valid number
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setSequenceMapping(prev => ({
        ...prev,
        productPrice: value
      }));
    }
  };

  // Handler for sub-objective dropdown
  const handleSubObjectiveChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as 'Revenue' | 'Volume';
    setSequenceMapping(prev => ({
      ...prev,
      subObjective: value
    }));
  };

  // Handlers for Contextual Value Angles section
  const [activeEmail, setActiveEmail] = useState<number>(0);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [isScrollNeeded, setIsScrollNeeded] = useState<boolean>(false);

  // Effect to check if scrolling is needed when the content email count changes
  useEffect(() => {
    const checkIfScrollNeeded = () => {
      if (tabsContainerRef.current) {
        // Check if scroll width is greater than client width
        const isNeeded = tabsContainerRef.current.scrollWidth > tabsContainerRef.current.clientWidth;
        setIsScrollNeeded(isNeeded);
      }
    };

    // Initial check
    checkIfScrollNeeded();

    // Set up a resize observer to check whenever the container size changes
    const resizeObserver = new ResizeObserver(checkIfScrollNeeded);
    
    if (tabsContainerRef.current) {
      resizeObserver.observe(tabsContainerRef.current);
    }

    // Clean up
    return () => {
      if (tabsContainerRef.current) {
        resizeObserver.unobserve(tabsContainerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [contextualValueAngles.contentEmailCount, activeEmail, tabsContainerRef]); 

  // Handle content email count change
  const handleContentEmailCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    
    // Allow empty input
    if (value === '') {
      setContextualValueAngles(prev => ({
        ...prev,
        contentEmailCount: NaN // Use NaN to represent empty input
      }));
      return;
    }
    
    // Only allow non-negative integers
    const regex = /^\d+$/;
    if (!regex.test(value)) return;
    
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      const newCount = numValue;
      
      setContextualValueAngles(prev => {
        const assignments = { ...prev.emailAssignments };
        
        // Add new emails if the count increased
        for (let i = 1; i <= newCount; i++) {
          if (!assignments[i]) {
            assignments[i] = [];
          }
        }
        
        // If count decreased, remove excess emails
        Object.keys(assignments).forEach(key => {
          const emailNum = parseInt(key);
          if (emailNum > newCount) {
            delete assignments[emailNum];
          }
        });
        
        return {
          contentEmailCount: newCount,
          emailAssignments: assignments
        };
      });
      
      // Set active email to the first one if it's a valid number
      if (numValue > 0 && activeEmail === 0) {
        setActiveEmail(1);
      } else if (numValue === 0) {
        setActiveEmail(0);
      } else if (activeEmail > numValue) {
        setActiveEmail(numValue);
      }
    }
  };

  // Handle angle assignment for content emails
  const handleAngleAssignment = (emailNum: number, angle: string, isChecked: boolean) => {
    setContextualValueAngles(prev => {
      const assignments = { ...prev.emailAssignments };
      
      // Initialize array for this email if it doesn't exist
      if (!assignments[emailNum]) {
        assignments[emailNum] = [];
      }
      
      // Add or remove the angle
      if (isChecked && !assignments[emailNum].includes(angle)) {
        assignments[emailNum] = [...assignments[emailNum], angle];
      } else if (!isChecked && assignments[emailNum].includes(angle)) {
        assignments[emailNum] = assignments[emailNum].filter(item => item !== angle);
      }
      
      return {
        ...prev,
        emailAssignments: assignments
      };
    });
  };

  // Handler for new angle input
  const handleNewAngleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewAngle(e.target.value);
  };

  // Function to add custom angle
  const addCustomAngle = async () => {
    // Trim the input to remove whitespace
    const trimmedAngle = newAngle.trim();
    
    // Validate the input
    if (!trimmedAngle) {
      setAddAngleError('Please enter an angle name');
      return;
    }
    
    if (trimmedAngle.length > 50) {
      setAddAngleError('Angle name must be 50 characters or less');
      return;
    }
    
    if (allAngles.includes(trimmedAngle)) {
      setAddAngleError('This angle already exists');
      return;
    }
    
    // Clear any previous errors
    setAddAngleError('');
    
    // Set loading state
    setIsAddingAngle(true);
    
    try {
      // Save the angle with the user ID
      const result = await saveCustomAngle(trimmedAngle, user?.id);
      
      if (result.success) {
        // Add to local state
        setCustomAngles(prev => [...prev, trimmedAngle]);
        
        // Clear the input
        setNewAngle('');
        
        // If an email is active, automatically assign the new angle to it
        if (activeEmail > 0) {
          handleAngleAssignment(activeEmail, trimmedAngle, true);
        }
      } else {
        setAddAngleError(result.message);
      }
    } catch (error) {
      console.error('Error adding custom angle:', error);
      setAddAngleError('Failed to add custom angle. Please try again.');
    } finally {
      setIsAddingAngle(false);
    }
  };

  // Sequence Mapping - Drag and Drop functionality
  const handleItemSelect = (itemId: string) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Remove an item completely
  const removeItem = (itemId: string) => {
    // Get the item
    const item = items[itemId];
    if (!item) return;
    
    // Remove from items list
    setItems(prev => {
      const newItems = { ...prev };
      delete newItems[itemId];
      return newItems;
    });
    
    // Remove from selected items if it was selected
    if (selectedItems[itemId]) {
      setSelectedItems(prev => {
        const newSelected = { ...prev };
        delete newSelected[itemId];
        return newSelected;
      });
    }
    
    // Remove from any ungrouped lists
    if (item.type === 'objection') {
      setUngroupedObjections(prev => prev.filter(id => id !== itemId));
    } else {
      setUngroupedBenefits(prev => prev.filter(id => id !== itemId));
    }
    
    // Remove from any groups
    setGroups(prev => {
      const newGroups = { ...prev };
      
      // Check each group for the itemId
      Object.keys(newGroups).forEach(groupId => {
        const group = newGroups[groupId];
        if (group.itemIds.includes(itemId)) {
          // Update the group by removing this item
          newGroups[groupId] = {
            ...group,
            itemIds: group.itemIds.filter(id => id !== itemId)
          };
          
          // If the group is now empty, remove it entirely
          if (newGroups[groupId].itemIds.length === 0) {
            delete newGroups[groupId];
            // Also update the group order
            setGroupOrder(prev => prev.filter(id => id !== groupId));
          }
        }
      });
      
      return newGroups;
    });
  };

  // Handle objections text input
  const handleObjectionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setObjectionsText(e.target.value);
  };

  // Handle benefits text input
  const handleBenefitsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBenefitsText(e.target.value);
  };

  // Parse objections from text input
  const parseObjections = () => {
    // Get objections line by line
    const objectionLines = objectionsText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    
    if (objectionLines.length === 0) return;
    
    // Create new items
    const newItems: Record<string, Item> = {};
    const newUngroupedObjections: string[] = [];
    
    objectionLines.forEach(text => {
      const id = `objection-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
      newItems[id] = {
        id,
        type: 'objection',
        content: text
      };
      newUngroupedObjections.push(id);
    });
    
    // Update state
    setItems(prev => ({
      ...prev,
      ...newItems
    }));
    
    setUngroupedObjections(prev => [
      ...prev,
      ...newUngroupedObjections
    ]);
    
    // Clear the input
    setObjectionsText('');
  };

  // Parse benefits from text input
  const parseBenefits = () => {
    // Get benefits line by line
    const benefitLines = benefitsText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    
    if (benefitLines.length === 0) return;
    
    // Create new items
    const newItems: Record<string, Item> = {};
    const newUngroupedBenefits: string[] = [];
    
    benefitLines.forEach(text => {
      const id = `benefit-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
      newItems[id] = {
        id,
        type: 'benefit',
        content: text
      };
      newUngroupedBenefits.push(id);
    });
    
    // Update state
    setItems(prev => ({
      ...prev,
      ...newItems
    }));
    
    setUngroupedBenefits(prev => [
      ...prev,
      ...newUngroupedBenefits
    ]);
    
    // Clear the input
    setBenefitsText('');
  };

  // Create a new group from selected items
  const createNewGroup = () => {
    // Get selected item IDs
    const selectedItemIds = Object.keys(selectedItems).filter(id => selectedItems[id]);
    
    if (selectedItemIds.length === 0) return;
    
    // Create a new group
    const groupId = `group-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    
    setGroups(prev => ({
      ...prev,
      [groupId]: {
        id: groupId,
        itemIds: selectedItemIds
      }
    }));
    
    // Add to group order
    setGroupOrder(prev => [...prev, groupId]);
    
    // Remove selected items from ungrouped lists
    setUngroupedObjections(prev => 
      prev.filter(id => !selectedItems[id])
    );
    
    setUngroupedBenefits(prev => 
      prev.filter(id => !selectedItems[id])
    );
    
    // Clear selected items
    setSelectedItems({});
  };

  // Handle drag and drop
  const handleDragEnd = (result: DropResult) => {
    const { source, destination, type, draggableId } = result;
    
    // Dropped outside a droppable area
    if (!destination) return;
    
    // Dropped in the same place
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }
    
    // Reordering groups
    if (type === 'group') {
      const newGroupOrder = Array.from(groupOrder);
      newGroupOrder.splice(source.index, 1);
      newGroupOrder.splice(destination.index, 0, draggableId);
      
      setGroupOrder(newGroupOrder);
      return;
    }
    
    // Moving an item within the same group or list
    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === 'ungrouped-objections') {
        const newUngroupedObjections = Array.from(ungroupedObjections);
        newUngroupedObjections.splice(source.index, 1);
        newUngroupedObjections.splice(destination.index, 0, draggableId);
        
        setUngroupedObjections(newUngroupedObjections);
        return;
      }
      
      if (source.droppableId === 'ungrouped-benefits') {
        const newUngroupedBenefits = Array.from(ungroupedBenefits);
        newUngroupedBenefits.splice(source.index, 1);
        newUngroupedBenefits.splice(destination.index, 0, draggableId);
        
        setUngroupedBenefits(newUngroupedBenefits);
        return;
      }
      
      // Reordering items within a group
      const group = groups[source.droppableId];
      if (group) {
        const newItemIds = Array.from(group.itemIds);
        newItemIds.splice(source.index, 1);
        newItemIds.splice(destination.index, 0, draggableId);
        
        setGroups(prev => ({
          ...prev,
          [source.droppableId]: {
            ...group,
            itemIds: newItemIds
          }
        }));
      }
      return;
    }
    
    // Moving an item to a different group or list
    
    // From ungrouped objections
    if (source.droppableId === 'ungrouped-objections') {
      const newUngroupedObjections = Array.from(ungroupedObjections);
      newUngroupedObjections.splice(source.index, 1);
      setUngroupedObjections(newUngroupedObjections);
      
      // To a group
      if (destination.droppableId !== 'ungrouped-objections' && destination.droppableId !== 'ungrouped-benefits') {
        const destGroup = groups[destination.droppableId];
        if (destGroup) {
          const newItemIds = Array.from(destGroup.itemIds);
          newItemIds.splice(destination.index, 0, draggableId);
          
          setGroups(prev => ({
            ...prev,
            [destination.droppableId]: {
              ...destGroup,
              itemIds: newItemIds
            }
          }));
        }
      } 
      // To ungrouped benefits (type conversion not allowed)
      else {
        // Put it back to ungrouped objections
        setUngroupedObjections(prev => {
          const newList = Array.from(prev);
          newList.splice(destination.index, 0, draggableId);
          return newList;
        });
      }
      return;
    }
    
    // From ungrouped benefits
    if (source.droppableId === 'ungrouped-benefits') {
      const newUngroupedBenefits = Array.from(ungroupedBenefits);
      newUngroupedBenefits.splice(source.index, 1);
      setUngroupedBenefits(newUngroupedBenefits);
      
      // To a group
      if (destination.droppableId !== 'ungrouped-objections' && destination.droppableId !== 'ungrouped-benefits') {
        const destGroup = groups[destination.droppableId];
        if (destGroup) {
          const newItemIds = Array.from(destGroup.itemIds);
          newItemIds.splice(destination.index, 0, draggableId);
          
          setGroups(prev => ({
            ...prev,
            [destination.droppableId]: {
              ...destGroup,
              itemIds: newItemIds
            }
          }));
        }
      } 
      // To ungrouped objections (type conversion not allowed)
      else {
        // Put it back to ungrouped benefits
        setUngroupedBenefits(prev => {
          const newList = Array.from(prev);
          newList.splice(destination.index, 0, draggableId);
          return newList;
        });
      }
      return;
    }
    
    // From a group
    const sourceGroup = groups[source.droppableId];
    if (sourceGroup) {
      const newSourceItemIds = Array.from(sourceGroup.itemIds);
      newSourceItemIds.splice(source.index, 1);
      
      // Update source group
      setGroups(prev => ({
        ...prev,
        [source.droppableId]: {
          ...sourceGroup,
          itemIds: newSourceItemIds
        }
      }));
      
      // To another group
      if (destination.droppableId !== 'ungrouped-objections' && destination.droppableId !== 'ungrouped-benefits') {
        const destGroup = groups[destination.droppableId];
        if (destGroup) {
          const newDestItemIds = Array.from(destGroup.itemIds);
          newDestItemIds.splice(destination.index, 0, draggableId);
          
          setGroups(prev => ({
            ...prev,
            [destination.droppableId]: {
              ...destGroup,
              itemIds: newDestItemIds
            }
          }));
        }
      } 
      // To ungrouped lists
      else {
        // Check item type and add to appropriate list
        const item = items[draggableId];
        if (item) {
          if (item.type === 'objection') {
            setUngroupedObjections(prev => {
              const newList = Array.from(prev);
              newList.splice(destination.index, 0, draggableId);
              return newList;
            });
          } else {
            setUngroupedBenefits(prev => {
              const newList = Array.from(prev);
              newList.splice(destination.index, 0, draggableId);
              return newList;
            });
          }
        }
      }
      
      // Remove empty groups
      if (newSourceItemIds.length === 0) {
        setGroups(prev => {
          const newGroups = { ...prev };
          delete newGroups[source.droppableId];
          return newGroups;
        });
        
        setGroupOrder(prev => prev.filter(id => id !== source.droppableId));
      }
    }
  };

  // Handle post-creation actions
  const handlePostCreationAction = (action: 'view' | 'create' | 'edit') => {
    switch (action) {
      case 'view':
        // Navigate to calendar view
        navigate('/campaigns');
        break;
      case 'create':
        // Reset the form and start a new campaign
        window.location.reload(); // Simple approach for now
        break;
      case 'edit':
        // Navigate to edit the first campaign (content if exists, otherwise promotion)
        if (contentCampaignId) {
          navigate(`/campaigns/edit/${contentCampaignId}`);
        } else {
          // Would navigate to promotion campaign edit, but we don't have its ID
          navigate('/campaigns');
        }
        break;
    }
    setShowPostCreationOptions(false);
  };

 // State for date overlap error is already declared above
  
  // Use effect to validate campaign duration whenever relevant state changes
  useEffect(() => {
    // Only check if we have a start date
    if (offerCreation.startDate) {
      const hasOverlap = validateCampaignDuration();
      if (hasOverlap) {
        const campaignDuration = calculateCampaignDuration();
        const endDate = new Date(offerCreation.startDate.getTime() + (campaignDuration * 24 * 60 * 60 * 1000));
        setCampaignOverlapError(
          `This campaign (${offerCreation.startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}) overlaps with an existing campaign. Please choose a different start date.`
        );
      } else {
        setCampaignOverlapError(null);
      }
    }
  }, [offerCreation.startDate, contextualValueAngles.contentEmailCount, Object.keys(groups).length, validateCampaignDuration, calculateCampaignDuration]);

  // Return JSX - find where the fixed footer is rendered and add condition
  return (
    <div className="container mx-auto px-4 py-8 pb-24">
      <h1 className="text-2xl font-bold text-[#111817] mb-6">
        {isEditMode ? "Edit Email Campaign" : "Create Email Campaign"}
      </h1>
      
      {/* Offer Creation Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
        <div 
          className="flex justify-between items-center p-6 cursor-pointer border-b border-gray-100"
          onClick={() => toggleSection('offerCreation')}
        >
          <div className="flex items-center">
            <h2 className="text-xl font-semibold text-gray-900">1. Offer Creation</h2>
            {!validationState.offerCreation.isValid && (
              <span className="ml-2 text-amber-500" title="This section requires attention">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </span>
            )}
            {validationState.offerCreation.isValid && (
              <span className="ml-2 text-green-500" title="This section is complete">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </span>
            )}
          </div>
          <span className="text-gray-500">
            {expandedSections.offerCreation ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </span>
        </div>
        
        {expandedSections.offerCreation && (
          <div className="p-6 space-y-6">
            {/* Campaign Name Input */}
            <div className="mt-4">
              <label htmlFor="campaignName" className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="campaignName"
                name="campaignName"
                value={offerCreation.campaignName}
                onChange={handleCampaignNameChange}
                placeholder="Enter a name for your campaign"
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                required
                maxLength={100}
              />
              <div className="mt-1 text-xs text-gray-500 flex justify-end">
                {offerCreation.campaignName.length}/100 characters
              </div>
              {/* Removed validation error message while keeping validation logic intact */}
            </div>

            {/* Clear Objective Input */}
            <div className="mt-4">
              <label htmlFor="objective" className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Objective <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="objective"
                name="objective"
                value={offerCreation.objective}
                onChange={handleObjectiveChange}
                placeholder="Increasing revenue, Boosting customer loyalty, Generating cash flow, Launching a new product"
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                required
                maxLength={100}
              />
              <div className="mt-1 text-xs text-gray-500 flex justify-end">
                {offerCreation.objective.length}/100 characters
              </div>
            </div>

            {/* Start Date Field - moved right after objective */}
            <div className="mt-4">
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Start Date <span className="text-red-500">*</span>
              </label>
              <DatePicker
                id="startDate"
                selected={offerCreation.startDate}
                onChange={(date: Date | null) => {
                  if (date) {
                    handleStartDateChange(date);
                  }
                }}
                dateFormat="MM/dd/yyyy"
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                placeholderText="Select a date"
                required
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                minDate={new Date() > new Date('2025-01-01') ? new Date() : new Date('2025-01-01')} // Use today or Jan 2025, whichever is later
                maxDate={new Date(new Date().setMonth(new Date().getMonth() + 13))} // Allow only up to 13 months ahead, matching dashboard calendar
              />
              <div className="mt-1 text-xs text-gray-500">
                You can only schedule campaigns between today and 13 months in the future.
              </div>
              {!validationState.offerCreation.hasStartDate && (
                <p className="text-red-500 text-sm mt-1">Please select a start date for your campaign</p>
              )}
              {/* Add date overlap error message directly under the date input */}
              {dateSelectionOverlapError && (
                <p className="text-red-500 text-sm mt-1">{dateSelectionOverlapError}</p>
              )}
            </div>
            
            {/* Level of Effort Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose Desired Level of Effort (LOE) <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    id="loe-low"
                    name="loe"
                    type="radio"
                    value="Low"
                    checked={offerCreation.loeChoice === 'Low'}
                    onChange={handleLOEChange}
                    className="h-4 w-4 border-gray-300 text-[#1E3B8C] focus:ring-[#1E3B8C]"
                  />
                  <label htmlFor="loe-low" className="ml-3 block text-sm font-medium text-gray-700">
                    Low
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="loe-medium"
                    name="loe"
                    type="radio"
                    value="Medium"
                    checked={offerCreation.loeChoice === 'Medium'}
                    onChange={handleLOEChange}
                    className="h-4 w-4 border-gray-300 text-[#1E3B8C] focus:ring-[#1E3B8C]"
                  />
                  <label htmlFor="loe-medium" className="ml-3 block text-sm font-medium text-gray-700">
                    Medium
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="loe-high"
                    name="loe"
                    type="radio"
                    value="High"
                    checked={offerCreation.loeChoice === 'High'}
                    onChange={handleLOEChange}
                    className="h-4 w-4 border-gray-300 text-[#1E3B8C] focus:ring-[#1E3B8C]"
                  />
                  <label htmlFor="loe-high" className="ml-3 block text-sm font-medium text-gray-700">
                    High
                  </label>
                </div>
              </div>
            </div>
            
            {/* Offer Ingredients Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Offer Ingredients <span className="text-red-500">*</span>
              </label>
              
              {/* Added scroll indicator text */}
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-500">Select all that apply</span>
                <span className="text-xs text-gray-500">Scroll to see more options</span>
              </div>
              
              {/* Enhanced scrollbar visibility with custom scrollbar styling */}
              <div className="relative">
                <div 
                  className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3 scrollbar-container"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#CBD5E1 #F1F5F9'
                  }}
                >
                  {/* Predefined ingredients */}
                  <div className="mb-2">
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Predefined Ingredients</h4>
                    {predefinedIngredients.map((ingredient, index) => (
                      <div key={`predefined-${index}`} className="flex items-center py-2">
                        <input
                          id={`ingredient-predefined-${index}`}
                          name="ingredients"
                          type="checkbox"
                          value={ingredient}
                          checked={offerCreation.offerIngredients.includes(ingredient)}
                          onChange={() => handleIngredientToggle(ingredient)}
                          className="h-4 w-4 border-gray-300 text-[#1E3B8C] focus:ring-[#1E3B8C] rounded"
                        />
                        <label htmlFor={`ingredient-predefined-${index}`} className="ml-3 block text-sm text-gray-700">
                          {ingredient}
                        </label>
                      </div>
                    ))}
                  </div>
                  
                  {/* Custom ingredients - only show section if there are custom ingredients */}
                  {customIngredients.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Your Custom Ingredients</h4>
                      {customIngredients.map((ingredient, index) => (
                        <div key={`custom-${index}`} className="flex items-center py-2">
                          <div className="flex items-center flex-1">
                            <input
                              id={`ingredient-custom-${index}`}
                              name="ingredients"
                              type="checkbox"
                              value={ingredient}
                              checked={offerCreation.offerIngredients.includes(ingredient)}
                              onChange={() => handleIngredientToggle(ingredient)}
                              className="h-4 w-4 border-gray-300 text-[#1E3B8C] focus:ring-[#1E3B8C] rounded"
                            />
                            <label htmlFor={`ingredient-custom-${index}`} className="ml-3 block text-sm text-gray-700">
                              {ingredient}
                            </label>
                          </div>
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-600 bg-transparent ml-1 h-4 w-4 flex items-center justify-center text-sm leading-none"
                            onClick={() => deleteIngredient(ingredient)}
                            aria-label={`Delete ${ingredient}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Visual scroll indicators - top and bottom shadows */}
                <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-white to-transparent pointer-events-none rounded-t-lg"></div>
                <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent pointer-events-none rounded-b-lg"></div>
              </div>
              
              {offerCreation.offerIngredients.length === 0 && (
                <p className="mt-2 text-xs text-amber-600">Please select at least one offer ingredient</p>
              )}
              
              {/* Selected count indicator */}
              {offerCreation.offerIngredients.length > 0 && (
                <p className="mt-2 text-xs text-green-600">
                  {offerCreation.offerIngredients.length} ingredient{offerCreation.offerIngredients.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>
            
            {/* Custom Ingredient Input */}
            <div className="pt-2">
              <label htmlFor="new-ingredient" className="block text-sm font-medium text-gray-700 mb-2">
                Add Your Own Custom Ingredient
              </label>
              <div className="flex items-center">
                <div className="w-1/2">
                  <input
                    type="text"
                    id="new-ingredient"
                    value={newIngredient}
                    onChange={handleNewIngredientChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter a custom offer ingredient"
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                    maxLength={50}
                    disabled={isAddingIngredient}
                  />
                </div>
                <div className="ml-3">
                  <button
                    type="button"
                    onClick={addCustomIngredient}
                    disabled={isAddingIngredient || !newIngredient.trim()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#1E3B8C] hover:bg-[#19307a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAddingIngredient ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Adding...
                      </>
                    ) : (
                      'Add Ingredient'
                    )}
                  </button>
                </div>
              </div>
              
              {/* Character count */}
              <div className="mt-1 text-xs text-gray-500 flex justify-end">
                {newIngredient.length}/50 characters
              </div>
              
              {/* Error message */}
              {addIngredientError && (
                <p className="mt-2 text-xs text-red-600">{addIngredientError}</p>
              )}
              
              <p className="mt-2 text-xs text-gray-500">
                Custom ingredients will be saved to your account for future campaigns
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* LOE Calculator Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
        <div 
          className="flex justify-between items-center p-6 cursor-pointer border-b border-gray-100"
          onClick={() => toggleSection('loeCalculator')}
        >
          <div className="flex items-center">
            <h2 className="text-xl font-semibold text-gray-900">2. LOE Calculator</h2>
            <span className="ml-2 text-gray-500 text-sm">(Optional)</span>
            {!validationState.loeCalculator.isValid && (
              <span className="ml-2 text-gray-400" title="This section is optional">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </span>
            )}
            {validationState.loeCalculator.isValid && (
              <span className="ml-2 text-green-500" title="This section is complete">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </span>
            )}
          </div>
          <span className="text-gray-500">
            {expandedSections.loeCalculator ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </span>
        </div>
        
        {expandedSections.loeCalculator && (
          <div className="p-6 space-y-6">
            <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <span className="font-medium">Note:</span> This calculator is optional and helps you estimate the potential return and effort level of your campaign. The calculations are not saved with your campaign data.
                  </p>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Enter your campaign details below to calculate the Level of Effort (LOE) and potential return.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Number of Email Clicks Input */}
              <div>
                <label htmlFor="emailClicks" className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Email Clicks
                </label>
                <input
                  type="text"
                  id="emailClicks"
                  name="emailClicks"
                  value={loeCalculator.emailClicks}
                  onChange={(e) => handlePositiveIntegerChange(e, 'emailClicks')}
                  placeholder="e.g., 1000"
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                />
                <p className="mt-1 text-xs text-gray-500">Positive whole numbers only</p>
              </div>
              
              {/* Offer Conversion Rate Input */}
              <div>
                <label htmlFor="conversionRate" className="block text-sm font-medium text-gray-700 mb-1">
                  Offer Conversion Rate (%)
                </label>
                <input
                  type="text"
                  id="conversionRate"
                  name="conversionRate"
                  value={loeCalculator.conversionRate}
                  onChange={handlePercentageChange}
                  placeholder="e.g., 2.5"
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                />
                <p className="mt-1 text-xs text-gray-500">Enter a percentage between 0-100</p>
              </div>
              
              {/* Average Order Value Input */}
              <div>
                <label htmlFor="averageOrderValue" className="block text-sm font-medium text-gray-700 mb-1">
                  Price or Average Order Value ($)
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="text"
                    id="averageOrderValue"
                    name="averageOrderValue"
                    value={loeCalculator.averageOrderValue}
                    onChange={(e) => handleCurrencyChange(e, 'averageOrderValue')}
                    placeholder="0.00"
                    className="block w-full pl-7 rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Enter the dollar amount with up to 2 decimal places</p>
              </div>
              
              {/* Unit Cost Input */}
              <div>
                <label htmlFor="unitCost" className="block text-sm font-medium text-gray-700 mb-1">
                  Unit Cost ($)
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="text"
                    id="unitCost"
                    name="unitCost"
                    value={loeCalculator.unitCost}
                    onChange={(e) => handleCurrencyChange(e, 'unitCost')}
                    placeholder="0.00"
                    className="block w-full pl-7 rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Cost per unit or product</p>
              </div>
              
              {/* Offer Hard Costs Input */}
              <div>
                <label htmlFor="hardCosts" className="block text-sm font-medium text-gray-700 mb-1">
                  Offer Fixed Costs ($)
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="text"
                    id="hardCosts"
                    name="hardCosts"
                    value={loeCalculator.hardCosts}
                    onChange={(e) => handleCurrencyChange(e, 'hardCosts')}
                    placeholder="0.00"
                    className="block w-full pl-7 rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Direct expenses related to your offer</p>
              </div>
              
              {/* Personnel Costs Input */}
              <div>
                <label htmlFor="personnelCosts" className="block text-sm font-medium text-gray-700 mb-1">
                  Personnel Costs ($)
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="text"
                    id="personnelCosts"
                    name="personnelCosts"
                    value={loeCalculator.personnelCosts}
                    onChange={(e) => handleCurrencyChange(e, 'personnelCosts')}
                    placeholder="0.00"
                    className="block w-full pl-7 rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Staff costs for implementation</p>
              </div>
              
              {/* Days to Implement Input */}
              <div>
                <label htmlFor="daysToImplement" className="block text-sm font-medium text-gray-700 mb-1">
                  Days to Implement
                </label>
                <input
                  type="text"
                  id="daysToImplement"
                  name="daysToImplement"
                  value={loeCalculator.daysToImplement}
                  onChange={(e) => handlePositiveIntegerChange(e, 'daysToImplement')}
                  placeholder="e.g., 7"
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                />
                <p className="mt-1 text-xs text-gray-500">Positive whole numbers only</p>
              </div>
              
              {/* Relationship Score Value Dropdown */}
              <div>
                <label htmlFor="relationshipScore" className="block text-sm font-medium text-gray-700 mb-1">
                  List Relationship
                </label>
                <select
                  id="relationshipScore"
                  name="relationshipScore"
                  value={loeCalculator.relationshipScore}
                  onChange={handleRelationshipScoreChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                >
                  {relationshipScoreOptions.map(option => (
                    <option key={option.value} value={option.value} className="text-gray-900">{option.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">How strong is your relationship with your list?</p>
              </div>
              
              {/* Opportunity Cost Preset Dropdown */}
              <div>
                <label htmlFor="opportunityCostPreset" className="block text-sm font-medium text-gray-700 mb-1">
                  Opportunity Cost
                </label>
                <select
                  id="opportunityCostPreset"
                  name="opportunityCostPreset"
                  value={loeCalculator.opportunityCostPreset}
                  onChange={handleOpportunityCostChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                >
                  {opportunityCostOptions.map((option, index) => (
                    <option key={index} value={option} className="text-gray-900">{option}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Cost of not doing other projects</p>
              </div>
            </div>
            
            {/* Opportunity Cost Preset Legend */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Opportunity Cost Legend:</h3>
              <div className="space-y-2 text-xs text-gray-600">
                <p><span className="font-semibold">Low:</span> Very low importance, no rush, minimal impact on other projects</p>
                <p><span className="font-semibold">Medium:</span> Average importance, moderate timeline pressure, some impact on other projects</p>
                <p><span className="font-semibold">High:</span> High importance, urgent timeline, significant impact on other priorities</p>
              </div>
            </div>
            
            {/* Results section */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Calculated Results:</h3>
              
              {!calculatedResults.hasValidInputs ? (
                <p className="text-sm text-amber-600 mb-2">
                  Please fill in all required fields to see calculation results.
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Offer Revenue Potential */}
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                      <p className="text-xs font-medium text-gray-500 mb-1">Offer Revenue Potential</p>
                      <p className="text-lg font-semibold text-[#1E3B8C]">
                        {formatCurrency(calculatedResults.revenueTotal)}
                      </p>
                    </div>
                    
                    {/* Level of Effort (LOE) */}
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                      <p className="text-xs font-medium text-gray-500 mb-1">Level of Effort (LOE)</p>
                      <div>
                        <p className="text-lg font-semibold">
                          {calculatedResults.loeCategory && (
                            <span 
                              className={`
                                ${calculatedResults.loeCategory === 'Low' ? 'text-green-600' : ''}
                                ${calculatedResults.loeCategory === 'Medium' ? 'text-amber-500' : ''}
                                ${calculatedResults.loeCategory === 'High' ? 'text-red-600' : ''}
                              `}
                            >
                              {calculatedResults.loeCategory}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Selected in Step 1:&nbsp;
                          <span 
                            className={`
                              font-medium
                              ${offerCreation.loeChoice === 'Low' ? 'text-green-600' : ''}
                              ${offerCreation.loeChoice === 'Medium' ? 'text-amber-500' : ''}
                              ${offerCreation.loeChoice === 'High' ? 'text-red-600' : ''}
                            `}
                          >
                            {offerCreation.loeChoice}
                          </span>
                        </p>
                      </div>
                    </div>
                    
                    {/* Email Marketing Profit */}
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                      <p className="text-xs font-medium text-gray-500 mb-1">Campaign Profit Potential</p>
                      <p className={`text-lg font-semibold ${calculatedResults.marketingProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(calculatedResults.marketingProfit)}
                      </p>
                    </div>
                  </div>
                  
                  {calculatedResults.marketingProfit < 0 && (
                    <p className="text-sm text-red-600 italic">
                      Warning: This campaign is projected to lose money. Consider adjusting your parameters.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Sequence Mapping Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
        <div 
          className="flex justify-between items-center p-6 cursor-pointer border-b border-gray-100"
          onClick={() => toggleSection('sequenceMapping')}
        >
          <div className="flex items-center">
            <h2 className="text-xl font-semibold text-gray-900">3. Sequence Mapping</h2>
            {!validationState.sequenceMapping.isValid && (
              <span className="ml-2 text-amber-500" title="This section requires attention">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </span>
            )}
            {validationState.sequenceMapping.isValid && (
              <span className="ml-2 text-green-500" title="This section is complete">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </span>
            )}
          </div>
          <span className="text-gray-500">
            {expandedSections.sequenceMapping ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </span>
        </div>
        
        {expandedSections.sequenceMapping && (
          <div className="p-6 space-y-6">
            <p className="text-sm text-gray-600 mb-4">
              Define the key elements of your email sequence to map out your campaign structure.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Name */}
              <div>
                <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="productName"
                  name="productName"
                  value={sequenceMapping.productName}
                  onChange={(e) => handleSequenceTextChange(e, 'productName', 100)}
                  placeholder="e.g., Premium Membership"
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                  required
                  maxLength={100}
                />
                <div className="mt-1 text-xs text-gray-500 flex justify-end">
                  {sequenceMapping.productName.length}/100 characters
                </div>
              </div>
              
              {/* Product Price */}
              <div>
                <label htmlFor="productPrice" className="block text-sm font-medium text-gray-700 mb-1">
                  Product Price <span className="text-red-500">*</span>
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="text"
                    id="productPrice"
                    name="productPrice"
                    value={sequenceMapping.productPrice}
                    onChange={handleProductPriceChange}
                    placeholder="0.00"
                    className="block w-full pl-7 rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Enter the dollar amount with up to 2 decimal places</p>
              </div>
              
              {/* Unique Mechanism */}
              <div className="md:col-span-2">
                <label htmlFor="uniqueMechanism" className="block text-sm font-medium text-gray-700 mb-1">
                  Unique Mechanism <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="uniqueMechanism"
                  name="uniqueMechanism"
                  value={sequenceMapping.uniqueMechanism}
                  onChange={(e) => handleSequenceTextChange(e, 'uniqueMechanism', 200)}
                  placeholder="e.g., Provide your secret sauce"
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                  required
                  maxLength={200}
                />
                <div className="mt-1 text-xs text-gray-500 flex justify-end">
                  {sequenceMapping.uniqueMechanism.length}/200 characters
                </div>
              </div>
              
              {/* Result They Want */}
              <div className="md:col-span-2">
                <label htmlFor="resultTheyWant" className="block text-sm font-medium text-gray-700 mb-1">
                  Result They Want & Conditions They Want <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="resultTheyWant"
                  name="resultTheyWant"
                  value={sequenceMapping.resultTheyWant}
                  onChange={(e) => handleSequenceTextChange(e, 'resultTheyWant', 200)}
                  placeholder="e.g., Double their website traffic while maintaining their current conversion rates and workload"
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                  required
                  maxLength={200}
                />
                <div className="mt-1 text-xs text-gray-500 flex justify-end">
                  {sequenceMapping.resultTheyWant.length}/200 characters
                </div>
              </div>
              
              {/* Main Belief */}
              <div className="md:col-span-2">
                <label htmlFor="mainBelief" className="block text-sm font-medium text-gray-700 mb-1">
                  Main Belief To Purchase <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="mainBelief"
                  name="mainBelief"
                  value={sequenceMapping.mainBelief}
                  onChange={(e) => handleSequenceTextChange(e, 'mainBelief', 200)}
                  placeholder="e.g., What they need to believe to buy"
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                  required
                  maxLength={200}
                />
                <div className="mt-1 text-xs text-gray-500 flex justify-end">
                  {sequenceMapping.mainBelief.length}/200 characters
                </div>
              </div>
              
              {/* Reason For Promo */}
              <div className="md:col-span-2">
                <label htmlFor="reasonForPromo" className="block text-sm font-medium text-gray-700 mb-1">
                  Reason For Promo (Theme/Angle) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="reasonForPromo"
                  name="reasonForPromo"
                  value={sequenceMapping.reasonForPromo}
                  onChange={(e) => handleSequenceTextChange(e, 'reasonForPromo', 200)}
                  placeholder="e.g., Limited time anniversary celebration"
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                  required
                  maxLength={200}
                />
                <div className="mt-1 text-xs text-gray-500 flex justify-end">
                  {sequenceMapping.reasonForPromo.length}/200 characters
                </div>
              </div>
              
              {/* Promotion Objective */}
              <div>
                <label htmlFor="subObjective" className="block text-sm font-medium text-gray-700 mb-1">
                  Promotion Objective <span className="text-red-500">*</span>
                </label>
                <select
                  id="subObjective"
                  name="subObjective"
                  value={sequenceMapping.subObjective}
                  onChange={handleSubObjectiveChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base text-gray-900 border-gray-300 focus:outline-none focus:ring-[#1E3B8C] focus:border-[#1E3B8C] sm:text-sm rounded-lg"
                  required
                >
                  <option value="Revenue">Revenue</option>
                  <option value="Volume">Volume</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Specific focus of the promotion campaign</p>
              </div>
            </div>
            
            {/* Objections and Intangible Benefits sections */}
            <div className="mt-6 space-y-8">
              <DragDropContext onDragEnd={handleDragEnd}>
                {/* Objections Section */}
                <div className="p-5 bg-gray-50 rounded-lg">
                  <h3 className="text-base font-medium text-gray-800 mb-4">Objections or Angles</h3>
                  <div className="mb-4">
                    <label htmlFor="objections-textarea" className="block text-sm font-medium text-gray-700 mb-1">
                      Add Potential Customer Objections Or Marketing Angles
                    </label>
                    <div className="mt-1">
                      <textarea
                        id="objections-textarea"
                        name="objections"
                        rows={4}
                        value={objectionsText}
                        onChange={handleObjectionsChange}
                        className="shadow-sm focus:ring-[#1E3B8C] focus:border-[#1E3B8C] block w-full sm:text-sm border-gray-300 rounded-md text-gray-900"
                        placeholder="Enter each objection or angle on a new line"
                      />
                    </div>
                    <div className="mt-2 flex justify-between">
                      <p className="text-xs text-gray-500">One objection or angle per line. These will become draggable items.</p>
                      <button
                        type="button"
                        onClick={parseObjections}
                        disabled={!objectionsText.trim()}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-[#1E3B8C] hover:bg-[#19307a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        Add Objections or Angles
                      </button>
                    </div>
                  </div>
                  
                  {ungroupedObjections.length > 0 && (
                    <div className="mt-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Your Objections or Angles</h4>
                        <div className="text-xs">
                          <span className="text-gray-500 mr-2">Drag to reorder or group</span>
                          <span className="text-gray-500">Select multiple to group</span>
                        </div>
                      </div>
                      
                      <div className="mb-2 flex items-center bg-amber-50 text-amber-800 text-xs p-2 rounded-md border border-amber-200">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>Ungrouped items will not be included in the campaign</span>
                      </div>
                      
                      <Droppable droppableId="ungrouped-objections">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="space-y-2"
                          >
                            {ungroupedObjections.map((itemId, index) => {
                              const item = items[itemId];
                              if (!item) return null;
                              
                              return (
                                <Draggable key={itemId} draggableId={itemId} index={index}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className="flex items-center p-3 border border-gray-200 rounded-lg bg-white"
                                    >
                                      <input
                                        type="checkbox"
                                        id={`item-select-${itemId}`}
                                        className="h-4 w-4 text-[#1E3B8C] focus:ring-[#1E3B8C] border-gray-300 rounded"
                                        checked={selectedItems[itemId]}
                                        onChange={() => handleItemSelect(itemId)}
                                      />
                                      <div className="ml-3 flex-1">
                                        <p className="text-sm text-gray-800">{item.content}</p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => removeItem(itemId)}
                                        className="ml-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors focus:outline-none focus:ring-0"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  )}
                </div>
                
                {/* Intangible Benefits Section */}
                <div className="p-5 bg-gray-50 rounded-lg">
                  <h3 className="text-base font-medium text-gray-800 mb-4">Intangible Benefits</h3>
                  <div className="mb-4">
                    <label htmlFor="benefits-textarea" className="block text-sm font-medium text-gray-700 mb-1">
                      Add Intangible Benefits
                    </label>
                    <div className="mt-1">
                      <textarea
                        id="benefits-textarea"
                        name="benefits"
                        rows={4}
                        value={benefitsText}
                        onChange={handleBenefitsChange}
                        className="shadow-sm focus:ring-[#1E3B8C] focus:border-[#1E3B8C] block w-full sm:text-sm border-gray-300 rounded-md text-gray-900"
                        placeholder="Enter each benefit on a new line"
                      />
                    </div>
                    <div className="mt-2 flex justify-between">
                      <p className="text-xs text-gray-500">One benefit per line. These will become draggable items.</p>
                      <button
                        type="button"
                        onClick={parseBenefits}
                        disabled={!benefitsText.trim()}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-[#1E3B8C] hover:bg-[#19307a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        Add Benefits
                      </button>
                    </div>
                  </div>
                  
                  {ungroupedBenefits.length > 0 && (
                    <div className="mt-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Your Intangible Benefits</h4>
                        <div className="text-xs">
                          <span className="text-gray-500 mr-2">Drag to reorder or group</span>
                          <span className="text-gray-500">Select multiple to group</span>
                        </div>
                      </div>
                      
                      <div className="mb-2 flex items-center bg-amber-50 text-amber-800 text-xs p-2 rounded-md border border-amber-200">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>Ungrouped items will not be included in the campaign</span>
                      </div>
                      
                      <Droppable droppableId="ungrouped-benefits">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="space-y-2"
                          >
                            {ungroupedBenefits.map((itemId, index) => {
                              const item = items[itemId];
                              if (!item) return null;
                              
                              return (
                                <Draggable key={itemId} draggableId={itemId} index={index}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className="flex items-center p-3 border border-gray-200 rounded-lg bg-white"
                                    >
                                      <input
                                        type="checkbox"
                                        id={`item-select-${itemId}`}
                                        className="h-4 w-4 text-[#1E3B8C] focus:ring-[#1E3B8C] border-gray-300 rounded"
                                        checked={selectedItems[itemId]}
                                        onChange={() => handleItemSelect(itemId)}
                                      />
                                      <div className="ml-3 flex-1">
                                        <p className="text-sm text-gray-800">{item.content}</p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => removeItem(itemId)}
                                        className="ml-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors focus:outline-none focus:ring-0"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  )}
                </div>
                
                {/* Groups Section */}
                {groupOrder.length > 0 && (
                  <div className="p-5 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-base font-medium text-gray-800">Grouped Persuasion Factors</h3>
                      <div className="bg-indigo-100 text-indigo-800 font-medium text-sm px-3 py-1 rounded-full">
                        Current Promotion Duration: {groupOrder.length} day{groupOrder.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mb-4">
                      Groups can contain both Objections/Angles and Benefits. Each item can only be in one group at a time. Drag items between groups or use checkboxes to select and create new groups. Each group represents one promotional email day. The total number of groups determines how many days the promotion phase will run.
                    </div>
                    
                    <Droppable droppableId="group-list" type="GROUP">
                      {(provided) => (
                        <div 
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="space-y-4"
                        >
                          {groupOrder.map((groupId, groupIndex) => {
                            const group = groups[groupId];
                            if (!group) return null;
                            
                            return (
                              <Draggable key={groupId} draggableId={groupId} index={groupIndex}>
                                {(provided) => (
                                  <div 
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className="border border-indigo-200 rounded-lg bg-white overflow-hidden"
                                  >
                                    <div 
                                      {...provided.dragHandleProps}
                                      className="bg-indigo-100 p-2 flex justify-between items-center"
                                    >
                                      <h4 className="text-sm font-medium text-indigo-800">
                                        Email {groupIndex + 1} ({group.itemIds.length} items)
                                      </h4>
                                      <div className="text-xs text-indigo-600">Drag to reorder email</div>
                                    </div>
                                    
                                    <Droppable droppableId={groupId}>
                                      {(provided) => (
                                        <div
                                          {...provided.droppableProps}
                                          ref={provided.innerRef}
                                          className="p-3 space-y-2"
                                        >
                                          {group.itemIds.map((itemId, itemIndex) => {
                                            const item = items[itemId];
                                            if (!item) return null;
                                            
                                            // Color-code based on item type
                                            const typeBadgeClass = item.type === 'objection' 
                                              ? 'bg-red-100 text-red-800' 
                                              : 'bg-green-100 text-green-800';
                                            
                                            return (
                                              <Draggable key={itemId} draggableId={itemId} index={itemIndex}>
                                                {(provided) => (
                                                  <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className="flex items-center p-3 border border-gray-200 rounded-lg bg-white"
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      id={`group-item-${itemId}`}
                                                      className="h-4 w-4 text-[#1E3B8C] focus:ring-[#1E3B8C] border-gray-300 rounded"
                                                      checked={selectedItems[itemId]}
                                                      onChange={() => handleItemSelect(itemId)}
                                                    />
                                                    <div className="ml-3 flex-1">
                                                      <div className="flex items-center">
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeBadgeClass} mr-2`}>
                                                          {item.type === 'objection' ? 'Objection/Angle' : 'Benefit'}
                                                        </span>
                                                        <p className="text-sm text-gray-800">{item.content}</p>
                                                      </div>
                                                    </div>
                                                    <div className="flex items-center">
                                                      <button
                                                        type="button"
                                                        onClick={() => removeItemFromGroup(group.id, itemId)}
                                                        className="mr-2 p-1 rounded-full bg-white text-gray-500 hover:bg-blue-50 hover:text-blue-600 transition-colors focus:outline-none focus:ring-0 border border-gray-300"
                                                        aria-label="Return to ungrouped list"
                                                        title="Return to ungrouped list"
                                                      >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                                          <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                                        </svg>
                                                      </button>
                                                      <button
                                                        type="button"
                                                        onClick={() => removeItem(itemId)}
                                                        className="p-1 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors focus:outline-none focus:ring-0"
                                                        aria-label="Delete item"
                                                      >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                      </button>
                                                    </div>
                                                  </div>
                                                )}
                                              </Draggable>
                                            );
                                          })}
                                          {provided.placeholder}
                                        </div>
                                      )}
                                    </Droppable>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                )}
                
                {/* Group Selected Button */}
                {Object.keys(selectedItems).filter((id: string) => selectedItems[id] === true).length >= 1 && (
                  <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200 mt-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-medium text-gray-700">{Object.keys(selectedItems).filter((id: string) => selectedItems[id] === true).length} items selected</span>
                        <p className="text-xs text-gray-500 mt-1">
                          You can group these items together to organize related objections/angles and benefits.
                        </p>
                      </div>
                      <button
                        onClick={createNewGroup}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#1E3B8C] hover:bg-[#19307a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Create Email from Selected ({Object.keys(selectedItems).filter((id: string) => selectedItems[id] === true).length})
                      </button>
                    </div>
                  </div>
                )}
              </DragDropContext>
            </div>
          </div>
        )}
      </div>
      
      {/* Contextual Value Angles Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
        <div 
          className="flex justify-between items-center p-6 cursor-pointer border-b border-gray-100"
          onClick={() => toggleSection('contextualValueAngles')}
        >
          <div className="flex items-center">
            <h2 className="text-xl font-semibold text-gray-900">4. Contextual Value Angles</h2>
            {!validationState.contextualValueAngles.isValid && (
              <span className="ml-2 text-amber-500" title="This section requires attention">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </span>
            )}
            {validationState.contextualValueAngles.isValid && (
              <span className="ml-2 text-green-500" title="This section is complete">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </span>
            )}
          </div>
          <span className="text-gray-500">
            {expandedSections.contextualValueAngles ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </span>
        </div>
        
        {expandedSections.contextualValueAngles && (
          <div className="p-6">
            <div className="space-y-6">
              {/* Content Email Count */}
              <div>
                <label htmlFor="contentEmailCount" className="block text-sm font-medium text-gray-700 mb-1">
                  How many content emails should this sequence have? <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 max-w-xs">
                  <input
                    type="number"
                    id="contentEmailCount"
                    name="contentEmailCount"
                    min="0"
                    placeholder="Enter a number (0 or more)"
                    value={isNaN(contextualValueAngles.contentEmailCount) ? '' : contextualValueAngles.contentEmailCount}
                    onChange={handleContentEmailCountChange}
                    className="shadow-sm focus:ring-[#1E3B8C] focus:border-[#1E3B8C] block w-full sm:text-sm border-gray-300 rounded-md text-gray-900"
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter the number of content emails you want in your sequence. You must enter a value: 0 if you don't need any content emails, or a positive number if you do.
                </p>
                {!validationState.contextualValueAngles.contentEmailCount && (
                  <p className="mt-1 text-xs text-red-500">
                    Please enter a number (0 or more).
                  </p>
                )}
              </div>
              
              {/* Content vs. Promotion Phase Explanation */}
              <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">Campaign Structure</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>Your campaign will have two phases:</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>
                          <span className="font-medium">Content Phase:</span>{' '}
                          {isNaN(contextualValueAngles.contentEmailCount) ? (
                            <span className="italic text-blue-500">Not yet specified</span>
                          ) : contextualValueAngles.contentEmailCount === 0 ? (
                            <span>No content emails</span>
                          ) : (
                            <span>
                              {contextualValueAngles.contentEmailCount} content email
                              {contextualValueAngles.contentEmailCount !== 1 ? 's' : ''}
                            </span>
                          )}
                          {' '}(defined here)
                        </li>
                        <li>
                          <span className="font-medium">Promotion Phase:</span>{' '}
                          {groupOrder.length} promotional email{groupOrder.length !== 1 ? 's' : ''} (defined in Sequence Mapping)
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Email Content Assignment Section */}
              {contextualValueAngles.contentEmailCount > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Assign Contextual Value Angles to Content Emails</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Select which angles to include in each content email. You can assign multiple angles to each email.
                  </p>
                  
                  {/* Email Assignment Tabs */}
                  <div className="mb-6">
                    <div className="border-b border-gray-200">
                      {/* Tabs container with horizontal scrolling and explicit nav controls */}
                      <div className="relative">
                        {/* Left scroll button - only visible when scrolling is needed */}
                        {isScrollNeeded && (
                          <button 
                            className="absolute left-0 top-[40%] -translate-y-1/2 z-10 bg-white p-1 rounded-full shadow-sm border border-[#1d3a8b] text-[#1d3a8b] hover:text-white hover:bg-[#1d3a8b] focus:outline-none transition-colors duration-200"
                            onClick={() => {
                              const container = document.getElementById('email-tabs-container');
                              if (container) container.scrollBy({ left: -200, behavior: 'smooth' });
                            }}
                            aria-label="Scroll left"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.0}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                        )}
                        
                        {/* Scrollable container */}
                        <div 
                          id="email-tabs-container"
                          ref={tabsContainerRef}
                          className="overflow-x-auto pb-2 -mb-2 scrollbar-none mx-8 pl-1" 
                          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                          <nav className="-mb-px flex space-x-2 min-w-max">
                            {Array.from({ length: contextualValueAngles.contentEmailCount }, (_, i) => i + 1).map(emailNum => (
                              <button
                                key={emailNum}
                                type="button"
                                data-active={activeEmail === emailNum ? 'true' : 'false'}
                                className={`whitespace-nowrap py-2 px-4 border-b-2 font-medium text-sm rounded-t-md transition-all duration-150
                                  ${activeEmail === emailNum 
                                    ? 'ring-2 ring-white ring-offset-1 ring-offset-[#1d3a8b] z-10 scale-105 transform bg-[#2e51c1] border-[#2e51c1] text-white shadow-md' 
                                    : contextualValueAngles.emailAssignments[emailNum]?.length > 0
                                      ? 'border-[#1d3a8b] text-white bg-[#1d3a8b] shadow-sm'
                                      : 'border-transparent text-white bg-[#1d3a8b] hover:bg-[#19307a]'
                                  }`}
                                style={{
                                  transformOrigin: emailNum === 1 ? 'left center' : emailNum === contextualValueAngles.contentEmailCount ? 'right center' : 'center'
                                }}
                                onClick={() => {
                                  // Force immediate state update and render
                                  // Use a requestAnimationFrame to ensure the DOM updates
                                  requestAnimationFrame(() => {
                                    setActiveEmail(emailNum);
                                  });
                                }}
                              >
                                <div className="flex items-center">
                                  <span>Email {emailNum}</span>
                                  {contextualValueAngles.emailAssignments[emailNum]?.length > 0 ? (
                                    <span className="ml-1 text-xs bg-green-700 text-white rounded-full px-1 min-w-[20px] text-center">
                                      {contextualValueAngles.emailAssignments[emailNum].length}
                                    </span>
                                  ) : (
                                    <span className="ml-1 text-amber-300" title="No angles assigned">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                      </svg>
                                    </span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </nav>
                        </div>
                        
                        {/* Right scroll button - only visible when scrolling is needed */}
                        {isScrollNeeded && (
                          <button 
                            className="absolute right-0 top-[40%] -translate-y-1/2 z-10 bg-white p-1 rounded-full shadow-sm border border-[#1d3a8b] text-[#1d3a8b] hover:text-white hover:bg-[#1d3a8b] focus:outline-none transition-colors duration-200"
                            onClick={() => {
                              const container = document.getElementById('email-tabs-container');
                              if (container) container.scrollBy({ left: 200, behavior: 'smooth' });
                            }}
                            aria-label="Scroll right"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.0}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        )}
                      </div>
                      
                      {/* Validation message */}
                      {contextualValueAngles.contentEmailCount > 0 && !validationState.contextualValueAngles.hasAtLeastOneAngleAssigned && (
                        <div className="mt-2 flex items-start gap-2 text-amber-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <p className="text-sm">
                            <strong>Each content email must have at least one angle assigned.</strong> Please assign angles to all {contextualValueAngles.contentEmailCount} emails.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Email Assignment Section - Only show active email */}
                  <div className="space-y-8">
                    {activeEmail > 0 && activeEmail <= contextualValueAngles.contentEmailCount && (
                      <div key={activeEmail} className="bg-white p-4 rounded-lg border border-gray-200">
                        <h4 className="text-md font-medium text-gray-800 mb-3">
                          Content Email {activeEmail}
                        </h4>
                        
                        {/* Selected angles for this email */}
                        {contextualValueAngles.emailAssignments[activeEmail]?.length > 0 && (
                          <div className="mb-3">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Selected Angles:</h5>
                            <div className="flex flex-wrap gap-2">
                              {contextualValueAngles.emailAssignments[activeEmail].map(angle => (
                                <span 
                                  key={angle} 
                                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                                >
                                  {angle}
                                  <button
                                    type="button"
                                    className="ml-2 h-5 w-5 inline-flex items-center justify-center text-red-500 hover:text-red-600 bg-transparent rounded-full text-base font-bold"
                                    onClick={() => handleAngleAssignment(activeEmail, angle, false)}
                                    aria-label={`Remove ${angle}`}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Empty state notification */}
                        {(!contextualValueAngles.emailAssignments[activeEmail] || contextualValueAngles.emailAssignments[activeEmail].length === 0) && (
                          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-600 flex items-start gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <p className="font-medium">No angles assigned to Email {activeEmail}</p>
                              <p className="text-sm mt-1">Please select at least one angle from the options below to include in this email.</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Angle selection */}
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Available Angles:</h5>
                          
                          {/* Added scroll indicator text */}
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs text-gray-500">Select all that apply</span>
                            <span className="text-xs text-gray-500">Scroll to see more options</span>
                          </div>
                          
                          {/* Enhanced scrollbar visibility with custom scrollbar styling */}
                          <div className="relative">
                            <div 
                              className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1 scrollbar-container"
                              style={{
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#CBD5E1 #F1F5F9'
                              }}
                            >
                              {/* Predefined angles */}
                              <div className="mb-2">
                                <h4 className="text-sm font-medium text-gray-700 mb-1">Predefined Angles</h4>
                                {predefinedAngles.map(angle => (
                                  <div key={angle} className="flex items-center py-0.5">
                                    <div className="flex items-center flex-1">
                                      <input
                                        id={`angle-${activeEmail}-${angle.replace(/\s+/g, '-').toLowerCase()}`}
                                        name={`angle-${activeEmail}`}
                                        type="checkbox"
                                        checked={contextualValueAngles.emailAssignments[activeEmail]?.includes(angle) || false}
                                        onChange={(e) => handleAngleAssignment(activeEmail, angle, e.target.checked)}
                                        className="h-4 w-4 text-[#1E3B8C] focus:ring-[#1E3B8C] border-gray-300 rounded"
                                      />
                                      <label
                                        htmlFor={`angle-${activeEmail}-${angle.replace(/\s+/g, '-').toLowerCase()}`}
                                        className="ml-3 block text-sm text-gray-700"
                                      >
                                        {angle}
                                      </label>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Custom angles - only show section if there are custom angles */}
                              {customAngles.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <h4 className="text-sm font-medium text-gray-700 mb-1">Your Custom Angles</h4>
                                  {customAngles.map(angle => (
                                    <div key={angle} className="flex items-center py-0.5">
                                      <div className="flex items-center flex-1">
                                        <input
                                          id={`angle-${activeEmail}-${angle.replace(/\s+/g, '-').toLowerCase()}`}
                                          name={`angle-${activeEmail}`}
                                          type="checkbox"
                                          checked={contextualValueAngles.emailAssignments[activeEmail]?.includes(angle) || false}
                                          onChange={(e) => handleAngleAssignment(activeEmail, angle, e.target.checked)}
                                          className="h-4 w-4 text-[#1E3B8C] focus:ring-[#1E3B8C] border-gray-300 rounded"
                                        />
                                        <label
                                          htmlFor={`angle-${activeEmail}-${angle.replace(/\s+/g, '-').toLowerCase()}`}
                                          className="ml-3 block text-sm text-gray-700"
                                        >
                                          {angle}
                                        </label>
                                      </div>
                                      <button
                                        type="button"
                                        className="text-red-500 hover:text-red-600 bg-transparent ml-1 h-4 w-4 flex items-center justify-center text-sm leading-none"
                                        onClick={() => deleteAngle(angle)}
                                        aria-label={`Delete ${angle}`}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Visual scroll indicators - top and bottom shadows */}
                            <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-white to-transparent pointer-events-none rounded-t-lg"></div>
                            <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent pointer-events-none rounded-b-lg"></div>
                          </div>
                          
                          {/* Selected count indicator */}
                          {contextualValueAngles.emailAssignments[activeEmail]?.length > 0 && (
                            <p className="mt-2 text-xs text-green-600">
                              {contextualValueAngles.emailAssignments[activeEmail].length} angle{contextualValueAngles.emailAssignments[activeEmail].length > 1 ? 's' : ''} selected
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Add Custom Angle Section */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-800 mb-3">Add Custom Contextual Value Angle</h3>
                <div className="flex items-center">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={newAngle}
                      onChange={handleNewAngleChange}
                      placeholder="Enter a new angle..."
                      className="shadow-sm focus:ring-[#1E3B8C] focus:border-[#1E3B8C] block w-full sm:text-sm border-gray-300 rounded-md text-gray-900"
                      maxLength={50}
                      disabled={isAddingAngle}
                    />
                    {addAngleError && (
                      <p className="mt-1 text-xs text-red-600">{addAngleError}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={addCustomAngle}
                    disabled={isAddingAngle || !newAngle.trim()}
                    className="ml-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-[#1E3B8C] hover:bg-[#19307a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1E3B8C] disabled:opacity-50"
                  >
                    {isAddingAngle ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Adding...
                      </>
                    ) : (
                      "Add Angle"
                    )}
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Custom angles will be saved to your account and available for future campaigns.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Campaign Creation Progress and Post-Creation Options */}
      <div className="fixed bottom-0 left-0 right-0 z-10 bg-gray-100 py-4 shadow-lg border-t border-gray-200">
        <div className="container mx-auto px-4 flex flex-col items-center">
          {/* Validation status message */}
          {!validationState.allValid && !showPostCreationOptions && (
            <div className="mb-2 text-sm text-amber-600 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>Please complete all required fields in:</span>
              <span className="ml-1 font-medium">
                {!validationState.offerCreation.isValid && <span className="mr-1">Offer Creation,</span>}
                {!validationState.sequenceMapping.isValid && <span className="mr-1">Sequence Mapping,</span>}
                {!validationState.contextualValueAngles.isValid && <span>Contextual Value Angles</span>}
              </span>
            </div>
          )}
          
          {/* Campaign overlap error */}
          {campaignOverlapError && (
            <div className="text-red-600 font-medium mb-2 flex items-center">
              <svg className="h-5 w-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 011.414 0L10 8.586l1.293-1.293a1 1 0 111.414 1.414L11.414 10l1.293 1.293a1 1 0 01-1.414 1.414L10 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L8.586 10 7.293 8.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              {campaignOverlapError}
            </div>
          )}
          
          {/* Campaign Creation Progress */}
          {isCreatingCampaign && (
            <div className="mb-3 w-full max-w-md">
              <div className="flex justify-between text-sm text-gray-700 mb-1">
                <span>
                  {creationPhase === 'idle' && 'Preparing...'}
                  {creationPhase === 'preparing' && 'Initializing campaign...'}
                  {creationPhase === 'content' && 'Creating content campaign...'}
                  {creationPhase === 'promotion' && 'Creating promotion campaign...'}
                  {creationPhase === 'complete' && (
                    <span className="text-green-600 font-medium flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Campaign creation complete!
                    </span>
                  )}
                </span>
                <span>{Math.round(creationProgress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-700 ease-in-out"
                  style={{ width: `${creationProgress}%` }}
                ></div>
              </div>
            </div>
          )}
          
          {/* Post-creation options */}
          {showPostCreationOptions ? (
            <div className="flex flex-col items-center">
              <div className="text-center mb-3">
                <h3 className="text-lg font-medium text-gray-900">Campaign Created Successfully!</h3>
                <p className="text-sm text-gray-600">What would you like to do next?</p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => handlePostCreationAction('view')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#1E3B8C] hover:bg-[#19307a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1E3B8C]"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  View on Calendar
                </button>
                <button
                  onClick={() => handlePostCreationAction('create')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-[#1E3B8C] bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1E3B8C]"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Another Campaign
                </button>
                <button
                  onClick={() => handlePostCreationAction('edit')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-[#1E3B8C] bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1E3B8C]"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Campaigns
                </button>
              </div>
            </div>
          ) : (
            /* Create button */
            <button 
              className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-[#1E3B8C] rounded-md hover:bg-[#19307a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[#1E3B8C]"
              disabled={!validationState.allValid || isCreatingCampaign}
              onClick={handleCreateCampaign}
            >
              {isCreatingCampaign ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Campaign...
                </>
              ) : (
                "Create Campaign"
              )}
            </button>
          )}
          
          {/* Error display */}
          {creationError && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md w-full max-w-md">
              <div className="text-sm text-red-600">
                {creationError.general && <p className="mb-1">{creationError.general}</p>}
                {creationError.content && <p className="mb-1">{creationError.content}</p>}
                {creationError.promotion && <p>{creationError.promotion}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Create Campaign Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white p-4 border-t border-gray-200 flex justify-center">
        <div className="max-w-6xl w-full flex justify-between items-center">
          <div>
            {/* Campaign overlap error */}
            {campaignOverlapError && (
              <div className="text-red-600 font-medium mb-2 flex items-center">
                <svg className="h-5 w-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 011.414 0L10 8.586l1.293-1.293a1 1 0 111.414 1.414L11.414 10l1.293 1.293a1 1 0 01-1.414 1.414L10 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L8.586 10 7.293 8.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                {campaignOverlapError}
              </div>
            )}
            
            {creationError && (
              <div className="text-red-500 mb-2">
                {creationError.content && <div>{creationError.content}</div>}
                {creationError.promotion && <div>{creationError.promotion}</div>}
              </div>
            )}
          </div>
          
          <button
            type="button"
            className={`bg-blue-600 text-white px-6 py-2 rounded-md shadow-sm ${
              !validationState.allValid || isCreatingCampaign ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
            }`}
            disabled={!validationState.allValid || isCreatingCampaign}
            onClick={handleCreateCampaign}
          >
            {isCreatingCampaign 
              ? "Processing..." 
              : isEditMode 
                ? "Update Campaign" 
                : "Create Campaign"}
          </button>
        </div>
      </div>
      

    </div>
  );
};

export default RCFCampaignPlannerPage;
